import React, { useState, useEffect, useRef, useCallback } from 'react';
import ReactCrop from 'react-image-crop';
import 'react-image-crop/dist/ReactCrop.css';

const ICON_SIZES = [
  { value: 'sm', label: 'Small', px: 48 },
  { value: 'md', label: 'Medium', px: 64 },
  { value: 'lg', label: 'Large', px: 96 },
  { value: 'xl', label: 'XL', px: 128 },
];

const ImageManager = ({ API, adminToken, isDarkMode, showNotification }) => {
  const authHeaders = { 'Authorization': `Bearer ${adminToken}`, 'Content-Type': 'application/json' };
  const [activeTab, setActiveTab] = useState('icons');
  const [icons, setIcons] = useState([]);
  const [games, setGames] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [uploadMode, setUploadMode] = useState('single');
  const [uploadName, setUploadName] = useState('');
  const [uploadFile, setUploadFile] = useState(null);
  const [uploadPreview, setUploadPreview] = useState(null);
  const [bulkFiles, setBulkFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [selectMode, setSelectMode] = useState(false);
  const [selectedIcons, setSelectedIcons] = useState(new Set());
  const [bulkDeleteConfirm, setBulkDeleteConfirm] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [imgSettings, setImgSettings] = useState({ max_upload_mb: 10 });
  const fileInputRef = useRef(null);
  const bulkFileInputRef = useRef(null);

  // Avatar state
  const [avatarImages, setAvatarImages] = useState([]);
  const [avatarLoading, setAvatarLoading] = useState(false);
  const [avatarSearchQuery, setAvatarSearchQuery] = useState('');
  const [avatarSelectMode, setAvatarSelectMode] = useState(false);
  const [selectedAvatarImages, setSelectedAvatarImages] = useState(new Set());
  const [avatarDeleteConfirm, setAvatarDeleteConfirm] = useState(null);
  const [avatarBulkDeleteConfirm, setAvatarBulkDeleteConfirm] = useState(false);
  const [coreAvatars, setCoreAvatars] = useState([]);
  const avatarFileInputRef = useRef(null);
  const avatarBulkFileInputRef = useRef(null);

  // Folder state
  const [folders, setFolders] = useState([]);
  const [foldersLoading, setFoldersLoading] = useState(false);
  const [activeFolderId, setActiveFolderId] = useState(null);
  const [folderAssets, setFolderAssets] = useState([]);
  const [folderAssetsLoading, setFolderAssetsLoading] = useState(false);
  const [showCreateFolderModal, setShowCreateFolderModal] = useState(false);
  const [newFolderName, setNewFolderName] = useState('');
  const [newFolderIcon, setNewFolderIcon] = useState('📁');
  const [folderDeleteConfirm, setFolderDeleteConfirm] = useState(null);
  const [folderSearchQuery, setFolderSearchQuery] = useState('');
  const [folderSelectMode, setFolderSelectMode] = useState(false);
  const [selectedAssets, setSelectedAssets] = useState(new Set());
  const [folderBulkDeleteConfirm, setFolderBulkDeleteConfirm] = useState(false);
  const [assetDeleteConfirm, setAssetDeleteConfirm] = useState(null);
  const folderFileInputRef = useRef(null);
  const folderBulkFileInputRef = useRef(null);

  // Detail panel state (WordPress-style)
  const [detailImage, setDetailImage] = useState(null);
  const [detailType, setDetailType] = useState(null); // 'icon', 'avatar', 'asset'
  const [detailEditing, setDetailEditing] = useState({});

  // Watermark state
  const [showWatermarkModal, setShowWatermarkModal] = useState(false);
  const [watermarkImage, setWatermarkImage] = useState(null);
  const [watermarkPosition, setWatermarkPosition] = useState('bottom-right');
  const [watermarkOpacity, setWatermarkOpacity] = useState(50);
  const [watermarkApplying, setWatermarkApplying] = useState(false);
  const watermarkFileRef = useRef(null);

  // Move image state
  const [showMoveModal, setShowMoveModal] = useState(false);
  const [movingImage, setMovingImage] = useState(false);

  // Inline edit state
  const [editingNameId, setEditingNameId] = useState(null);
  const [editingNameValue, setEditingNameValue] = useState('');

  // Crop state
  const [showCropModal, setShowCropModal] = useState(false);
  const [cropMode, setCropMode] = useState('new'); // 'new' = new upload, 'existing' = crop existing
  const [cropTarget, setCropTarget] = useState('icon'); // 'icon' or 'avatar'
  const [cropIconId, setCropIconId] = useState(null); // id when cropping existing
  const [crop, setCrop] = useState(null);
  const [completedCrop, setCompletedCrop] = useState(null);
  const imgRef = useRef(null);
  const uploadLockRef = useRef(false);

  // Load folders and settings on mount
  useEffect(() => {
    loadFolders();
    loadImageSettings();
  }, []);

  useEffect(() => {
    if (activeTab === 'icons') {
      loadIcons();
      loadGames();
    }
    if (activeTab === 'avatars') {
      loadAvatarImages();
      loadCoreAvatars();
    }
    if (activeTab.startsWith('folder-')) {
      const folderId = activeTab.replace('folder-', '');
      setActiveFolderId(folderId);
      loadFolderAssets(folderId);
    }
    if (activeTab === 'settings') {
      loadImageSettings();
    }
  }, [activeTab]);

  const loadImageSettings = async () => {
    try {
      const res = await fetch(`${API}/admin/image-settings`, { headers: authHeaders });
      if (res.ok) {
        const data = await res.json();
        setImgSettings(data);
      }
    } catch (e) { console.error('Failed to load image settings', e); }
  };

  const saveImageSettings = async (updates) => {
    try {
      const res = await fetch(`${API}/admin/image-settings`, {
        method: 'PUT', headers: authHeaders, body: JSON.stringify(updates)
      });
      if (res.ok) {
        const data = await res.json();
        setImgSettings(prev => ({ ...prev, ...updates }));
        showNotification('Settings saved!', 'success');
      } else {
        const err = await res.json().catch(() => ({}));
        showNotification(err.detail || 'Failed to save', 'error');
      }
    } catch (e) { showNotification('Failed to save settings', 'error'); }
  };

  const loadIcons = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/admin/icons`, {
        headers: { 'Authorization': `Bearer ${adminToken}` }
      });
      if (res.ok) {
        const data = await res.json();
        setIcons(data.icons || []);
      }
    } catch (err) {
      console.error('Error loading icons:', err);
    }
    setLoading(false);
  };

  const loadGames = async () => {
    try {
      const allGames = [];
      const res = await fetch(`${API}/admin/games`, {
        headers: { 'Authorization': `Bearer ${adminToken}` }
      });
      if (res.ok) {
        const data = await res.json();
        allGames.push(...(data.games || []));
      }

      const specialGames = [
        { id: 'conversation-challenge', name: 'Conversation Challenge' },
        { id: 'souk-bargain-game', name: 'Souk Bargain Game' },
        { id: 'souk-clothes-game', name: 'Souk L7wayej (Clothes)' },
        { id: 'morocco-journey-game', name: 'Morocco Journey' },
        { id: 'scene-explorer', name: 'Scene Explorer' },
        { id: 'word-puzzle', name: 'Word Puzzle' },
        { id: 'learning-clock', name: 'Learning Clock' },
      ];

      const existingIds = new Set(allGames.map(g => g.id));
      for (const sg of specialGames) {
        if (!existingIds.has(sg.id)) {
          allGames.push(sg);
        }
      }

      setGames(allGames);
    } catch (err) {
      console.error('Error loading games:', err);
    }
  };

  // ========== CROP LOGIC ==========

  // Open crop modal for a NEW upload
  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const allowed = ['image/png', 'image/jpeg', 'image/webp'];
    if (!allowed.includes(file.type)) {
      showNotification('Only PNG, JPEG, and WebP files are allowed', 'error');
      return;
    }
    if (file.size > (imgSettings.max_upload_mb || 10) * 1024 * 1024) {
      showNotification(`File too large. Max ${imgSettings.max_upload_mb || 10}MB`, 'error');
      return;
    }

    setUploadFile(file);
    const reader = new FileReader();
    reader.onload = (ev) => {
      setUploadPreview(ev.target.result);
      setCropMode('new');
      setCropTarget('icon');
      setCropIconId(null);
      setShowCropModal(true);
      setCrop(null);
      setCompletedCrop(null);
    };
    reader.readAsDataURL(file);

    if (!uploadName) {
      const nameWithoutExt = file.name.replace(/\.[^/.]+$/, '').replace(/[_-]/g, ' ');
      setUploadName(nameWithoutExt);
    }
  };

  // Open crop modal for an EXISTING icon
  const openCropExisting = (icon) => {
    const url = getIconUrl(icon);
    setUploadPreview(url);
    setUploadName(icon.name);
    setCropMode('existing');
    setCropTarget('icon');
    setCropIconId(icon.id);
    setShowCropModal(true);
    setCrop(null);
    setCompletedCrop(null);
  };

  const onImageLoad = useCallback((e) => {
    imgRef.current = e.currentTarget;
    // Use percent-based crop so it stays valid when image is resized via zoom
    const initialCrop = { unit: '%', x: 10, y: 10, width: 80, height: 80 };
    setCrop(initialCrop);
    // completedCrop must be in pixels for getCroppedImageBlob — compute from current rendered size
    const { width, height } = e.currentTarget;
    setCompletedCrop({ unit: 'px', x: width * 0.1, y: height * 0.1, width: width * 0.8, height: height * 0.8 });
  }, []);

  const getCroppedImageBlob = () => {
    return new Promise((resolve) => {
      if (!completedCrop || !imgRef.current) {
        resolve(null);
        return;
      }
      const image = imgRef.current;
      const canvas = document.createElement('canvas');
      const scaleX = image.naturalWidth / image.width;
      const scaleY = image.naturalHeight / image.height;
      canvas.width = completedCrop.width * scaleX;
      canvas.height = completedCrop.height * scaleY;
      const ctx = canvas.getContext('2d');
      ctx.drawImage(
        image,
        completedCrop.x * scaleX,
        completedCrop.y * scaleY,
        completedCrop.width * scaleX,
        completedCrop.height * scaleY,
        0, 0, canvas.width, canvas.height
      );
      canvas.toBlob((blob) => resolve(blob), 'image/png', 1);
    });
  };

  // Crop & Upload (new file) — routes to icon or avatar endpoint based on cropTarget
  const handleCropAndUpload = async () => {
    if (uploadLockRef.current) return;
    uploadLockRef.current = true;

    if (!uploadName.trim()) {
      showNotification('Please enter a name', 'error');
      uploadLockRef.current = false;
      return;
    }

    const isAvatar = cropTarget === 'avatar';
    const isAsset = cropTarget === 'asset';
    let uploadUrl, category;
    if (isAsset) {
      uploadUrl = `${API}/admin/image-assets/${activeFolderId}/upload`;
      category = 'asset';
    } else if (isAvatar) {
      uploadUrl = `${API}/admin/avatar-images/upload`;
      category = 'avatar';
    } else {
      uploadUrl = `${API}/admin/icons/upload`;
      category = 'game-icon';
    }

    setUploading(true);
    try {
      const croppedBlob = await getCroppedImageBlob();
      if (!croppedBlob) {
        showNotification('Please select a crop area', 'error');
        setUploading(false);
        return;
      }

      const fileName = uploadFile ? uploadFile.name : `${uploadName.trim()}.png`;
      const fileToUpload = new File([croppedBlob], fileName, { type: 'image/png' });

      const formData = new FormData();
      formData.append('file', fileToUpload);
      formData.append('name', uploadName.trim());
      if (!isAsset) formData.append('category', category);

      const res = await fetch(uploadUrl, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${adminToken}` },
        body: formData
      });

      if (res.ok) {
        const data = await res.json();

        // If cropping an existing item, re-assign then delete old
        if (cropMode === 'existing' && cropIconId) {
          if (isAsset) {
            // For assets, delete the old one (new one already created)
            await fetch(`${API}/admin/image-assets/${cropIconId}`, {
              method: 'DELETE',
              headers: { 'Authorization': `Bearer ${adminToken}` }
            });
          } else if (isAvatar) {
            const newImg = data.avatar_image;
            const oldImg = avatarImages.find(i => i.id === cropIconId);
            const assignedAvatars = oldImg
              ? (Array.isArray(oldImg.assigned_to) ? oldImg.assigned_to : (oldImg.assigned_to ? [oldImg.assigned_to] : []))
              : [];
            for (const avatarId of assignedAvatars) {
              await fetch(`${API}/admin/avatar-images/${newImg.id}/assign`, {
                method: 'POST',
                headers: { ...authHeaders },
                body: JSON.stringify({ avatar_id: avatarId })
              });
            }
            if (oldImg?.avatar_size) {
              await fetch(`${API}/admin/avatar-images/${newImg.id}`, {
                method: 'PUT',
                headers: { ...authHeaders },
                body: JSON.stringify({ avatar_size: oldImg.avatar_size })
              });
            }
            await fetch(`${API}/admin/avatar-images/${cropIconId}`, {
              method: 'DELETE',
              headers: { 'Authorization': `Bearer ${adminToken}` }
            });
          } else {
            const newIcon = data.icon;
            const oldIcon = icons.find(i => i.id === cropIconId);
            const assignedGames = oldIcon
              ? (Array.isArray(oldIcon.assigned_to) ? oldIcon.assigned_to : (oldIcon.assigned_to ? [oldIcon.assigned_to] : []))
              : [];
            for (const gameId of assignedGames) {
              await fetch(`${API}/admin/icons/${newIcon.id}/assign`, {
                method: 'POST',
                headers: { ...authHeaders },
                body: JSON.stringify({ game_id: gameId })
              });
            }
            if (oldIcon?.icon_size) {
              await fetch(`${API}/admin/icons/${newIcon.id}`, {
                method: 'PUT',
                headers: { ...authHeaders },
                body: JSON.stringify({ icon_size: oldIcon.icon_size })
              });
            }
            await fetch(`${API}/admin/icons/${cropIconId}`, {
              method: 'DELETE',
              headers: { 'Authorization': `Bearer ${adminToken}` }
            });
          }
        }
        showNotification(`${isAsset ? 'Asset' : isAvatar ? 'Avatar' : 'Icon'} cropped & saved!`, 'success');
        closeCropModal();
        closeUploadModal();
        if (isAsset) { loadFolderAssets(activeFolderId); loadFolders(); }
        else if (isAvatar) loadAvatarImages();
        else loadIcons();
      } else {
        const err = await res.json();
        showNotification(err.detail || 'Upload failed', 'error');
      }
    } catch (err) {
      showNotification('Upload failed', 'error');
    }
    setUploading(false);
    uploadLockRef.current = false;
  };

  // Upload original (skip crop) — only for new uploads
  const handleUploadOriginal = async () => {
    if (uploadLockRef.current) return;
    uploadLockRef.current = true;

    if (!uploadFile || !uploadName.trim()) {
      showNotification('Please enter a name', 'error');
      uploadLockRef.current = false;
      return;
    }

    const isAvatar = cropTarget === 'avatar';
    const isAsset = cropTarget === 'asset';
    let uploadUrl, category;
    if (isAsset) {
      uploadUrl = `${API}/admin/image-assets/${activeFolderId}/upload`;
      category = 'asset';
    } else if (isAvatar) {
      uploadUrl = `${API}/admin/avatar-images/upload`;
      category = 'avatar';
    } else {
      uploadUrl = `${API}/admin/icons/upload`;
      category = 'game-icon';
    }

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', uploadFile);
      formData.append('name', uploadName.trim());
      if (!isAsset) formData.append('category', category);

      const res = await fetch(uploadUrl, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${adminToken}` },
        body: formData
      });

      if (res.ok) {
        showNotification(`${isAsset ? 'Asset' : isAvatar ? 'Avatar' : 'Icon'} uploaded successfully`, 'success');
        closeCropModal();
        closeUploadModal();
        if (isAsset) { loadFolderAssets(activeFolderId); loadFolders(); }
        else if (isAvatar) loadAvatarImages();
        else loadIcons();
      } else {
        const err = await res.json();
        showNotification(err.detail || 'Upload failed', 'error');
      }
    } catch (err) {
      showNotification('Upload failed', 'error');
    }
    setUploading(false);
    uploadLockRef.current = false;
  };

  const closeCropModal = () => {
    setShowCropModal(false);
    setCrop(null);
    setCompletedCrop(null);
    setCropMode('new');
    setCropTarget('icon');
    setCropIconId(null);
    imgRef.current = null;
    uploadLockRef.current = false;
  };

  // ========== BULK UPLOAD ==========

  const handleBulkFileSelect = (e) => {
    const files = Array.from(e.target.files);
    const allowed = ['image/png', 'image/jpeg', 'image/webp'];
    const valid = [];
    let skipped = 0;
    for (const file of files) {
      if (!allowed.includes(file.type)) { skipped++; continue; }
      if (file.size > (imgSettings.max_upload_mb || 10) * 1024 * 1024) { skipped++; continue; }
      valid.push(file);
    }
    setBulkFiles(valid);
    if (skipped > 0) {
      showNotification(`${skipped} file(s) skipped (wrong type or too large)`, 'error');
    }
  };

  const handleBulkUpload = async () => {
    if (uploadLockRef.current) return;
    uploadLockRef.current = true;

    if (bulkFiles.length === 0) {
      showNotification('Please select files to upload', 'error');
      uploadLockRef.current = false;
      return;
    }
    setUploading(true);
    try {
      const formData = new FormData();
      for (const file of bulkFiles) formData.append('files', file);
      formData.append('category', 'game-icon');

      const res = await fetch(`${API}/admin/icons/bulk-upload`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${adminToken}` },
        body: formData
      });
      if (res.ok) {
        const data = await res.json();
        showNotification(`${data.uploaded_count} icon(s) uploaded${data.error_count > 0 ? `, ${data.error_count} failed` : ''}`, 'success');
        closeUploadModal();
        loadIcons();
      } else {
        showNotification('Bulk upload failed', 'error');
      }
    } catch (err) {
      showNotification('Bulk upload failed', 'error');
    }
    setUploading(false);
    uploadLockRef.current = false;
  };

  const closeUploadModal = () => {
    setShowUploadModal(false);
    setUploadFile(null);
    setUploadPreview(null);
    setUploadName('');
    setBulkFiles([]);
    setUploadMode('single');
    uploadLockRef.current = false;
  };

  // ========== DELETE ==========

  const handleDelete = async (iconId) => {
    try {
      const res = await fetch(`${API}/admin/icons/${iconId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${adminToken}` }
      });
      if (res.ok) {
        showNotification('Icon deleted', 'success');
        setDeleteConfirm(null);
        loadIcons();
      }
    } catch (err) {
      showNotification('Delete failed', 'error');
    }
  };

  const handleBulkDelete = async () => {
    if (selectedIcons.size === 0) return;
    try {
      const res = await fetch(`${API}/admin/icons/bulk-delete`, {
        method: 'POST',
        headers: { ...authHeaders },
        body: JSON.stringify({ icon_ids: Array.from(selectedIcons) })
      });
      if (res.ok) {
        const data = await res.json();
        showNotification(`${data.deleted_count} icon(s) deleted`, 'success');
        setSelectedIcons(new Set());
        setSelectMode(false);
        setBulkDeleteConfirm(false);
        loadIcons();
      }
    } catch (err) {
      showNotification('Bulk delete failed', 'error');
    }
  };

  const toggleSelectIcon = (iconId) => {
    setSelectedIcons(prev => {
      const next = new Set(prev);
      if (next.has(iconId)) next.delete(iconId);
      else next.add(iconId);
      return next;
    });
  };

  const selectAll = () => {
    if (selectedIcons.size === icons.length) setSelectedIcons(new Set());
    else setSelectedIcons(new Set(icons.map(i => i.id)));
  };

  // ========== ASSIGN ==========

  const handleAssign = async (iconId, gameId) => {
    if (!gameId) return;
    try {
      const res = await fetch(`${API}/admin/icons/${iconId}/assign`, {
        method: 'POST',
        headers: { ...authHeaders },
        body: JSON.stringify({ game_id: gameId })
      });
      if (res.ok) {
        const gameName = games.find(g => g.id === gameId)?.name || gameId;
        showNotification(`Icon assigned to ${gameName}`, 'success');
        // Update in-place — no page refresh
        setIcons(prev => prev.map(icon => {
          if (icon.id !== iconId) return icon;
          const assigned = Array.isArray(icon.assigned_to) ? [...icon.assigned_to] : (icon.assigned_to ? [icon.assigned_to] : []);
          if (!assigned.includes(gameId)) assigned.push(gameId);
          return { ...icon, assigned_to: assigned };
        }));
      }
    } catch (err) {
      showNotification('Assign failed', 'error');
    }
  };

  const handleUnassign = async (iconId, gameId) => {
    try {
      await fetch(`${API}/admin/icons/${iconId}/unassign`, {
        method: 'POST',
        headers: { ...authHeaders },
        body: JSON.stringify({ game_id: gameId })
      });
      const gameName = games.find(g => g.id === gameId)?.name || gameId;
      showNotification(`Icon removed from ${gameName}`, 'success');
      // Update in-place — no page refresh
      setIcons(prev => prev.map(icon => {
        if (icon.id !== iconId) return icon;
        const assigned = Array.isArray(icon.assigned_to) ? icon.assigned_to.filter(g => g !== gameId) : [];
        return { ...icon, assigned_to: assigned };
      }));
    } catch (err) {
      showNotification('Unassign failed', 'error');
    }
  };

  // ========== SIZE ==========

  const handleSizeChange = async (iconId, newSize) => {
    try {
      const res = await fetch(`${API}/admin/icons/${iconId}`, {
        method: 'PUT',
        headers: { ...authHeaders },
        body: JSON.stringify({ icon_size: newSize })
      });
      if (res.ok) {
        const preset = ICON_SIZES.find(s => s.value === newSize);
        showNotification(`Icon size updated to ${preset ? preset.label : newSize + 'px'}`, 'success');
        setIcons(prev => prev.map(icon => icon.id === iconId ? { ...icon, icon_size: newSize } : icon));
      }
    } catch (err) {
      showNotification('Size update failed', 'error');
    }
  };

  const handleCustomSize = async (iconId, pxValue) => {
    const px = parseInt(pxValue);
    if (!px || px < 16 || px > 256) {
      showNotification('Size must be between 16 and 256 pixels', 'error');
      return;
    }
    try {
      const res = await fetch(`${API}/admin/icons/${iconId}`, {
        method: 'PUT',
        headers: { ...authHeaders },
        body: JSON.stringify({ icon_size: String(px) })
      });
      if (res.ok) {
        showNotification(`Icon size updated to ${px}px`, 'success');
        loadIcons();
      }
    } catch (err) {
      showNotification('Size update failed', 'error');
    }
  };

  const getIconUrl = (icon) => {
    const base = API.replace('/api', '');
    return `${base}${icon.url}`;
  };

  const getAvatarImageUrl = (img) => {
    const base = API.replace('/api', '');
    return `${base}${img.url}`;
  };

  // ========== AVATAR IMAGE FUNCTIONS ==========

  const loadAvatarImages = async () => {
    setAvatarLoading(true);
    try {
      const res = await fetch(`${API}/admin/avatar-images`, {
        headers: { 'Authorization': `Bearer ${adminToken}` }
      });
      if (res.ok) {
        const data = await res.json();
        setAvatarImages(data.avatar_images || []);
      }
    } catch (err) {
      console.error('Error loading avatar images:', err);
    }
    setAvatarLoading(false);
  };

  const loadCoreAvatars = async () => {
    try {
      const res = await fetch(`${API}/admin/avatars`, {
        headers: { 'Authorization': `Bearer ${adminToken}` }
      });
      if (res.ok) {
        const data = await res.json();
        setCoreAvatars(Array.isArray(data) ? data : []);
      }
    } catch (err) {
      console.error('Error loading core avatars:', err);
    }
  };

  const handleAvatarFileSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const allowed = ['image/png', 'image/jpeg', 'image/webp'];
    if (!allowed.includes(file.type)) {
      showNotification('Only PNG, JPEG, and WebP files are allowed', 'error');
      return;
    }
    if (file.size > (imgSettings.max_upload_mb || 10) * 1024 * 1024) {
      showNotification(`File too large. Max ${imgSettings.max_upload_mb || 10}MB`, 'error');
      return;
    }
    setUploadFile(file);
    const reader = new FileReader();
    reader.onload = (ev) => {
      setUploadPreview(ev.target.result);
      setCropMode('new');
      setCropTarget('avatar');
      setCropIconId(null);
      setShowCropModal(true);
      setCrop(null);
      setCompletedCrop(null);
    };
    reader.readAsDataURL(file);
    if (!uploadName) {
      const nameWithoutExt = file.name.replace(/\.[^/.]+$/, '').replace(/[_-]/g, ' ');
      setUploadName(nameWithoutExt);
    }
  };

  const openCropExistingAvatar = (img) => {
    const url = getAvatarImageUrl(img);
    setUploadPreview(url);
    setUploadName(img.name);
    setCropMode('existing');
    setCropTarget('avatar');
    setCropIconId(img.id);
    setShowCropModal(true);
    setCrop(null);
    setCompletedCrop(null);
  };

  const handleAvatarDelete = async (imageId) => {
    try {
      const res = await fetch(`${API}/admin/avatar-images/${imageId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${adminToken}` }
      });
      if (res.ok) {
        showNotification('Avatar image deleted', 'success');
        setAvatarDeleteConfirm(null);
        loadAvatarImages();
      }
    } catch (err) {
      showNotification('Delete failed', 'error');
    }
  };

  const handleAvatarBulkDelete = async () => {
    if (selectedAvatarImages.size === 0) return;
    try {
      const res = await fetch(`${API}/admin/avatar-images/bulk-delete`, {
        method: 'POST',
        headers: { ...authHeaders },
        body: JSON.stringify({ image_ids: Array.from(selectedAvatarImages) })
      });
      if (res.ok) {
        const data = await res.json();
        showNotification(`${data.deleted_count} avatar image(s) deleted`, 'success');
        setSelectedAvatarImages(new Set());
        setAvatarSelectMode(false);
        setAvatarBulkDeleteConfirm(false);
        loadAvatarImages();
      }
    } catch (err) {
      showNotification('Bulk delete failed', 'error');
    }
  };

  const handleAvatarBulkFileSelect = (e) => {
    const files = Array.from(e.target.files);
    const allowed = ['image/png', 'image/jpeg', 'image/webp'];
    const valid = [];
    let skipped = 0;
    for (const file of files) {
      if (!allowed.includes(file.type)) { skipped++; continue; }
      if (file.size > (imgSettings.max_upload_mb || 10) * 1024 * 1024) { skipped++; continue; }
      valid.push(file);
    }
    setBulkFiles(valid);
    if (skipped > 0) {
      showNotification(`${skipped} file(s) skipped (wrong type or too large)`, 'error');
    }
  };

  const handleAvatarBulkUpload = async () => {
    if (uploadLockRef.current) return;
    uploadLockRef.current = true;

    if (bulkFiles.length === 0) {
      showNotification('Please select files to upload', 'error');
      uploadLockRef.current = false;
      return;
    }
    setUploading(true);
    try {
      const formData = new FormData();
      for (const file of bulkFiles) formData.append('files', file);
      formData.append('category', 'avatar');
      const res = await fetch(`${API}/admin/avatar-images/bulk-upload`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${adminToken}` },
        body: formData
      });
      if (res.ok) {
        const data = await res.json();
        showNotification(`${data.uploaded_count} avatar(s) uploaded${data.error_count > 0 ? `, ${data.error_count} failed` : ''}`, 'success');
        closeUploadModal();
        loadAvatarImages();
      } else {
        showNotification('Bulk upload failed', 'error');
      }
    } catch (err) {
      showNotification('Bulk upload failed', 'error');
    }
    setUploading(false);
    uploadLockRef.current = false;
  };

  const toggleSelectAvatarImage = (imageId) => {
    setSelectedAvatarImages(prev => {
      const next = new Set(prev);
      if (next.has(imageId)) next.delete(imageId);
      else next.add(imageId);
      return next;
    });
  };

  const selectAllAvatarImages = () => {
    if (selectedAvatarImages.size === avatarImages.length) setSelectedAvatarImages(new Set());
    else setSelectedAvatarImages(new Set(avatarImages.map(i => i.id)));
  };

  const handleAvatarAssign = async (imageId, avatarId) => {
    if (!avatarId) return;
    try {
      const res = await fetch(`${API}/admin/avatar-images/${imageId}/assign`, {
        method: 'POST',
        headers: { ...authHeaders },
        body: JSON.stringify({ avatar_id: avatarId })
      });
      if (res.ok) {
        const avatarName = coreAvatars.find(a => a.id === avatarId)?.name || avatarId;
        showNotification(`Image assigned to ${avatarName}`, 'success');
        setAvatarImages(prev => prev.map(img => {
          if (img.id !== imageId) return img;
          const assigned = Array.isArray(img.assigned_to) ? [...img.assigned_to] : (img.assigned_to ? [img.assigned_to] : []);
          if (!assigned.includes(avatarId)) assigned.push(avatarId);
          return { ...img, assigned_to: assigned };
        }));
      }
    } catch (err) {
      showNotification('Assign failed', 'error');
    }
  };

  const handleAvatarUnassign = async (imageId, avatarId) => {
    try {
      await fetch(`${API}/admin/avatar-images/${imageId}/unassign`, {
        method: 'POST',
        headers: { ...authHeaders },
        body: JSON.stringify({ avatar_id: avatarId })
      });
      const avatarName = coreAvatars.find(a => a.id === avatarId)?.name || avatarId;
      showNotification(`Image removed from ${avatarName}`, 'success');
      setAvatarImages(prev => prev.map(img => {
        if (img.id !== imageId) return img;
        const assigned = Array.isArray(img.assigned_to) ? img.assigned_to.filter(a => a !== avatarId) : [];
        return { ...img, assigned_to: assigned };
      }));
    } catch (err) {
      showNotification('Unassign failed', 'error');
    }
  };

  const handleAvatarSizeChange = async (imageId, newSize) => {
    try {
      const res = await fetch(`${API}/admin/avatar-images/${imageId}`, {
        method: 'PUT',
        headers: { ...authHeaders },
        body: JSON.stringify({ avatar_size: newSize })
      });
      if (res.ok) {
        const preset = ICON_SIZES.find(s => s.value === newSize);
        showNotification(`Avatar size updated to ${preset ? preset.label : newSize + 'px'}`, 'success');
        setAvatarImages(prev => prev.map(img => img.id === imageId ? { ...img, avatar_size: newSize } : img));
      }
    } catch (err) {
      showNotification('Size update failed', 'error');
    }
  };

  const handleAvatarCustomSize = async (imageId, pxValue) => {
    const px = parseInt(pxValue);
    if (!px || px < 16 || px > 256) {
      showNotification('Size must be between 16 and 256 pixels', 'error');
      return;
    }
    try {
      const res = await fetch(`${API}/admin/avatar-images/${imageId}`, {
        method: 'PUT',
        headers: { ...authHeaders },
        body: JSON.stringify({ avatar_size: String(px) })
      });
      if (res.ok) {
        showNotification(`Avatar size updated to ${px}px`, 'success');
        loadAvatarImages();
      }
    } catch (err) {
      showNotification('Size update failed', 'error');
    }
  };

  // ========== INLINE NAME EDIT ==========

  const startEditName = (id, currentName) => {
    setEditingNameId(id);
    setEditingNameValue(currentName);
  };

  const saveInlineName = async (id, type) => {
    const name = editingNameValue.trim();
    if (!name) { setEditingNameId(null); return; }

    let url, refreshFn;
    if (type === 'icon') {
      url = `${API}/admin/icons/${id}`;
      refreshFn = () => setIcons(prev => prev.map(i => i.id === id ? { ...i, name } : i));
    } else if (type === 'avatar') {
      url = `${API}/admin/avatar-images/${id}`;
      refreshFn = () => setAvatarImages(prev => prev.map(i => i.id === id ? { ...i, name } : i));
    } else {
      url = `${API}/admin/image-assets/${id}`;
      refreshFn = () => setFolderAssets(prev => prev.map(i => i.id === id ? { ...i, name } : i));
    }

    try {
      await fetch(url, {
        method: 'PUT',
        headers: { ...authHeaders },
        body: JSON.stringify({ name })
      });
      refreshFn();
    } catch (err) {
      showNotification('Name update failed', 'error');
    }
    setEditingNameId(null);
  };

  // ========== DETAIL PANEL ==========

  const openDetailPanel = (item, type) => {
    setDetailImage(item);
    setDetailType(type);
    setDetailEditing({
      name: item.name || '',
      alt_text: item.alt_text || '',
      description: item.description || ''
    });
  };

  const closeDetailPanel = () => {
    setDetailImage(null);
    setDetailType(null);
    setDetailEditing({});
  };

  const saveDetailField = async (field, value) => {
    if (!detailImage) return;
    let url;
    if (detailType === 'icon') url = `${API}/admin/icons/${detailImage.id}`;
    else if (detailType === 'avatar') url = `${API}/admin/avatar-images/${detailImage.id}`;
    else url = `${API}/admin/image-assets/${detailImage.id}`;

    try {
      await fetch(url, {
        method: 'PUT',
        headers: { ...authHeaders },
        body: JSON.stringify({ [field]: value })
      });
      setDetailImage(prev => ({ ...prev, [field]: value }));
      // Update in list too
      if (detailType === 'icon') setIcons(prev => prev.map(i => i.id === detailImage.id ? { ...i, [field]: value } : i));
      else if (detailType === 'avatar') setAvatarImages(prev => prev.map(i => i.id === detailImage.id ? { ...i, [field]: value } : i));
      else setFolderAssets(prev => prev.map(i => i.id === detailImage.id ? { ...i, [field]: value } : i));
      showNotification('Updated', 'success');
    } catch (err) {
      showNotification('Update failed', 'error');
    }
  };

  const getImageUrl = (item) => {
    const base = API.replace('/api', '');
    return `${base}${item.url}`;
  };

  const handleDetailDownload = () => {
    if (!detailImage) return;
    const url = getImageUrl(detailImage);
    const a = document.createElement('a');
    a.href = url;
    a.download = detailImage.filename || detailImage.name || 'image';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  const handleDetailCrop = () => {
    if (!detailImage) return;
    const url = getImageUrl(detailImage);
    setUploadPreview(url);
    setUploadName(detailImage.name);
    setCropMode('existing');
    setCropTarget(detailType);
    setCropIconId(detailImage.id);
    setShowCropModal(true);
    setCrop(null);
    setCompletedCrop(null);
    closeDetailPanel();
  };

  const handleDetailDelete = async () => {
    if (!detailImage) return;
    let url;
    if (detailType === 'icon') url = `${API}/admin/icons/${detailImage.id}`;
    else if (detailType === 'avatar') url = `${API}/admin/avatar-images/${detailImage.id}`;
    else url = `${API}/admin/image-assets/${detailImage.id}`;

    try {
      const res = await fetch(url, { method: 'DELETE', headers: { 'Authorization': `Bearer ${adminToken}` } });
      if (res.ok) {
        showNotification('Deleted', 'success');
        closeDetailPanel();
        if (detailType === 'icon') loadIcons();
        else if (detailType === 'avatar') loadAvatarImages();
        else loadFolderAssets(activeFolderId);
      }
    } catch (err) {
      showNotification('Delete failed', 'error');
    }
  };

  // ========== MOVE IMAGE ==========

  const handleMoveImage = async (targetType, targetFolderId = null) => {
    if (!detailImage || !detailType) return;
    setMovingImage(true);
    try {
      const sourceType = detailType === 'asset' ? 'asset' : detailType;
      const body = {
        image_id: detailImage.id,
        source_type: sourceType,
        target_type: targetType === 'icon' || targetType === 'avatar' ? targetType : 'folder',
        target_folder_id: targetFolderId || undefined,
      };
      const res = await fetch(`${API}/admin/image-move`, {
        method: 'POST',
        headers: authHeaders,
        body: JSON.stringify(body),
      });
      const data = await res.json();
      if (res.ok) {
        showNotification('Image moved!', 'success');
        setShowMoveModal(false);
        closeDetailPanel();
        // Refresh source
        if (detailType === 'icon') loadIcons();
        else if (detailType === 'avatar') loadAvatarImages();
        else if (activeFolderTab) loadFolderAssets(activeFolderTab);
        // Refresh target
        if (targetType === 'icon') loadIcons();
        else if (targetType === 'avatar') loadAvatarImages();
        else if (targetFolderId) loadFolderAssets(targetFolderId);
        loadFolders(); // refresh asset counts
      } else {
        showNotification(data.detail || 'Move failed', 'error');
      }
    } catch (err) {
      showNotification('Move failed', 'error');
    }
    setMovingImage(false);
  };

  // ========== WATERMARK ==========

  const openWatermark = () => {
    setShowWatermarkModal(true);
    setWatermarkImage(null);
    setWatermarkPosition('bottom-right');
    setWatermarkOpacity(50);
  };

  const applyWatermark = async () => {
    if (!detailImage || !watermarkImage) return;
    setWatermarkApplying(true);
    try {
      const formData = new FormData();
      formData.append('watermark_file', watermarkImage);
      formData.append('position', watermarkPosition);
      formData.append('opacity', String(watermarkOpacity));

      const res = await fetch(`${API}/admin/image-watermark/${detailType}/${detailImage.id}`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${adminToken}` },
        body: formData
      });

      if (res.ok) {
        const data = await res.json();
        showNotification('Watermark applied!', 'success');
        setShowWatermarkModal(false);
        // Refresh to get updated image
        setDetailImage(prev => ({
          ...prev,
          file_size: data.file_size,
          dimensions: data.dimensions
        }));
        if (detailType === 'icon') loadIcons();
        else if (detailType === 'avatar') loadAvatarImages();
        else loadFolderAssets(activeFolderId);
      } else {
        const err = await res.json();
        showNotification(err.detail || 'Watermark failed', 'error');
      }
    } catch (err) {
      showNotification('Watermark failed', 'error');
    }
    setWatermarkApplying(false);
  };

  // ========== FOLDER FUNCTIONS ==========

  const loadFolders = async () => {
    setFoldersLoading(true);
    try {
      const res = await fetch(`${API}/admin/image-folders`, {
        headers: { 'Authorization': `Bearer ${adminToken}` }
      });
      if (res.ok) {
        const data = await res.json();
        setFolders(data.folders || []);
      }
    } catch (err) {
      console.error('Error loading folders:', err);
    }
    setFoldersLoading(false);
  };

  const loadFolderAssets = async (folderId) => {
    setFolderAssetsLoading(true);
    try {
      const res = await fetch(`${API}/admin/image-assets/${folderId}`, {
        headers: { 'Authorization': `Bearer ${adminToken}` }
      });
      if (res.ok) {
        const data = await res.json();
        setFolderAssets(data.assets || []);
      }
    } catch (err) {
      console.error('Error loading folder assets:', err);
    }
    setFolderAssetsLoading(false);
  };

  const createFolder = async () => {
    if (!newFolderName.trim()) {
      showNotification('Please enter a folder name', 'error');
      return;
    }
    try {
      const res = await fetch(`${API}/admin/image-folders`, {
        method: 'POST',
        headers: { ...authHeaders },
        body: JSON.stringify({ name: newFolderName.trim(), icon: newFolderIcon })
      });
      if (res.ok) {
        const data = await res.json();
        showNotification('Folder created!', 'success');
        setShowCreateFolderModal(false);
        setNewFolderName('');
        setNewFolderIcon('📁');
        await loadFolders();
        // Switch to the new folder tab
        if (data.folder?.id) {
          setActiveTab(`folder-${data.folder.id}`);
        }
      } else {
        const err = await res.json();
        showNotification(err.detail || 'Create failed', 'error');
      }
    } catch (err) {
      showNotification('Create failed', 'error');
    }
  };

  const deleteFolder = async (folderId) => {
    try {
      const res = await fetch(`${API}/admin/image-folders/${folderId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${adminToken}` }
      });
      if (res.ok) {
        showNotification('Folder deleted', 'success');
        setFolderDeleteConfirm(null);
        if (activeFolderId === folderId) {
          setActiveFolderId(null);
          setFolderAssets([]);
        }
        loadFolders();
      }
    } catch (err) {
      showNotification('Delete failed', 'error');
    }
  };

  const openFolder = (folder) => {
    setActiveFolderId(folder.id);
    loadFolderAssets(folder.id);
    setFolderSearchQuery('');
    setFolderSelectMode(false);
    setSelectedAssets(new Set());
  };

  const handleFolderAssetFileSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const allowed = ['image/png', 'image/jpeg', 'image/webp'];
    if (!allowed.includes(file.type)) {
      showNotification('Only PNG, JPEG, and WebP files are allowed', 'error');
      return;
    }
    if (file.size > (imgSettings.max_upload_mb || 10) * 1024 * 1024) {
      showNotification(`File too large. Max ${imgSettings.max_upload_mb || 10}MB`, 'error');
      return;
    }
    setUploadFile(file);
    const reader = new FileReader();
    reader.onload = (ev) => {
      setUploadPreview(ev.target.result);
      setCropMode('new');
      setCropTarget('asset');
      setCropIconId(null);
      setShowCropModal(true);
      setCrop(null);
      setCompletedCrop(null);
    };
    reader.readAsDataURL(file);
    if (!uploadName) {
      const nameWithoutExt = file.name.replace(/\.[^/.]+$/, '').replace(/[_-]/g, ' ');
      setUploadName(nameWithoutExt);
    }
  };

  const handleFolderBulkUpload = async () => {
    if (uploadLockRef.current) return;
    uploadLockRef.current = true;

    if (bulkFiles.length === 0 || !activeFolderId) {
      uploadLockRef.current = false;
      return;
    }
    setUploading(true);
    try {
      const formData = new FormData();
      for (const file of bulkFiles) formData.append('files', file);
      const res = await fetch(`${API}/admin/image-assets/${activeFolderId}/bulk-upload`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${adminToken}` },
        body: formData
      });
      if (res.ok) {
        const data = await res.json();
        showNotification(`${data.uploaded_count} asset(s) uploaded`, 'success');
        closeUploadModal();
        loadFolderAssets(activeFolderId);
        loadFolders();
      } else {
        showNotification('Bulk upload failed', 'error');
      }
    } catch (err) {
      showNotification('Bulk upload failed', 'error');
    }
    setUploading(false);
    uploadLockRef.current = false;
  };

  const handleAssetDelete = async (assetId) => {
    try {
      const res = await fetch(`${API}/admin/image-assets/${assetId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${adminToken}` }
      });
      if (res.ok) {
        showNotification('Asset deleted', 'success');
        setAssetDeleteConfirm(null);
        loadFolderAssets(activeFolderId);
        loadFolders();
      }
    } catch (err) {
      showNotification('Delete failed', 'error');
    }
  };

  const handleAssetBulkDelete = async () => {
    if (selectedAssets.size === 0) return;
    try {
      const res = await fetch(`${API}/admin/image-assets/bulk-delete`, {
        method: 'POST',
        headers: { ...authHeaders },
        body: JSON.stringify({ asset_ids: Array.from(selectedAssets) })
      });
      if (res.ok) {
        const data = await res.json();
        showNotification(`${data.deleted_count} asset(s) deleted`, 'success');
        setSelectedAssets(new Set());
        setFolderSelectMode(false);
        setFolderBulkDeleteConfirm(false);
        loadFolderAssets(activeFolderId);
        loadFolders();
      }
    } catch (err) {
      showNotification('Bulk delete failed', 'error');
    }
  };

  const FOLDER_EMOJIS = ['📁', '🖼️', '🎮', '🏆', '⭐', '🎨', '📷', '🎯', '💎', '🔥', '🌟', '📱', '🎵', '🎬', '📚', '🛒'];

  const tabs = [
    { id: 'icons', label: 'Icons' },
    { id: 'avatars', label: 'Avatars' },
    ...folders.map(f => ({ id: `folder-${f.id}`, label: `${f.icon || '📁'} ${f.name}`, folderId: f.id })),
    { id: 'settings', label: 'Settings' },
    { id: 'optimize', label: 'Optimize' },
  ];

  // Extract folder ID from active tab
  const activeFolderTab = activeTab.startsWith('folder-') ? activeTab.replace('folder-', '') : null;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h2 className={`text-2xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
          Image Manager
        </h2>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 flex-wrap items-center">
        {tabs.map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`px-4 py-2 rounded-t-lg font-medium text-sm transition-colors ${
              activeTab === tab.id
                ? isDarkMode
                  ? 'bg-gray-700 text-white border-b-2 border-blue-500'
                  : 'bg-white text-blue-600 border-b-2 border-blue-500'
                : isDarkMode
                  ? 'bg-gray-800 text-gray-400 hover:text-gray-200'
                  : 'bg-gray-100 text-gray-500 hover:text-gray-700'
            }`}
          >
            {tab.label}
          </button>
        ))}
        <button
          onClick={() => setShowCreateFolderModal(true)}
          className={`px-3 py-2 rounded-t-lg font-medium text-sm transition-colors ${
            isDarkMode
              ? 'bg-gray-800 text-green-400 hover:text-green-300 hover:bg-gray-700'
              : 'bg-gray-100 text-green-600 hover:text-green-700 hover:bg-green-50'
          }`}
          title="Create new folder"
        >
          + Folder
        </button>
      </div>

      {/* Icons Tab */}
      {activeTab === 'icons' && (
        <div className="space-y-4">
          {/* Action Bar */}
          <div className="flex items-center justify-between gap-2 flex-wrap">
            <div className="flex items-center gap-2">
              {icons.length > 0 && (
                <button
                  onClick={() => { setSelectMode(!selectMode); setSelectedIcons(new Set()); setBulkDeleteConfirm(false); }}
                  className={`px-3 py-2 rounded-lg font-medium text-sm ${
                    selectMode
                      ? 'bg-red-100 text-red-700 hover:bg-red-200'
                      : isDarkMode
                        ? 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                        : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  {selectMode ? 'Cancel Select' : 'Select'}
                </button>
              )}
              {selectMode && (
                <>
                  <button
                    onClick={selectAll}
                    className={`px-3 py-2 rounded-lg font-medium text-sm ${isDarkMode ? 'bg-gray-700 text-gray-300 hover:bg-gray-600' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
                  >
                    {selectedIcons.size === icons.length ? 'Deselect All' : 'Select All'}
                  </button>
                  {selectedIcons.size > 0 && !bulkDeleteConfirm && (
                    <button
                      onClick={() => setBulkDeleteConfirm(true)}
                      className="px-3 py-2 rounded-lg font-medium text-sm bg-red-600 text-white hover:bg-red-700"
                    >
                      Delete {selectedIcons.size} Selected
                    </button>
                  )}
                  {bulkDeleteConfirm && (
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-red-500 font-medium">Are you sure?</span>
                      <button
                        onClick={handleBulkDelete}
                        className="px-3 py-2 rounded-lg font-medium text-sm bg-red-600 text-white hover:bg-red-700"
                      >
                        Yes, Delete {selectedIcons.size}
                      </button>
                      <button
                        onClick={() => setBulkDeleteConfirm(false)}
                        className={`px-3 py-2 rounded-lg font-medium text-sm ${isDarkMode ? 'bg-gray-700 text-gray-300' : 'bg-gray-200 text-gray-600'}`}
                      >
                        No
                      </button>
                    </div>
                  )}
                </>
              )}
            </div>
            <button
              onClick={() => setShowUploadModal(true)}
              className="px-4 py-2 bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white rounded-lg font-medium text-sm shadow-md"
            >
              + Upload Icons
            </button>
          </div>

          {/* Search + Stats */}
          {icons.length > 0 && (() => {
            const assignedCount = icons.filter(i => {
              const a = Array.isArray(i.assigned_to) ? i.assigned_to : (i.assigned_to ? [i.assigned_to] : []);
              return a.length > 0;
            }).length;
            return (
              <div className="flex items-center gap-4 flex-wrap">
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search icons..."
                  className={`flex-1 min-w-[200px] px-3 py-2 rounded-lg border text-sm ${
                    isDarkMode
                      ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400'
                      : 'bg-white border-gray-200 text-gray-800 placeholder-gray-400'
                  }`}
                />
                <div className={`flex gap-3 text-xs font-medium ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                  <span>Total: {icons.length}</span>
                  <span className="text-green-500">Assigned: {assignedCount}</span>
                  <span className="text-orange-500">Unassigned: {icons.length - assignedCount}</span>
                </div>
              </div>
            );
          })()}

          {/* Icons Grid */}
          {loading ? (
            <div className="text-center py-12">
              <div className="animate-spin inline-block w-10 h-10 border-4 border-blue-500 border-t-transparent rounded-full"></div>
            </div>
          ) : icons.length === 0 ? (
            <div className={`rounded-xl p-12 text-center ${isDarkMode ? 'bg-gray-800 border border-gray-700' : 'bg-white border border-gray-200'}`}>
              <div className="text-5xl mb-4">📁</div>
              <h3 className={`text-lg font-bold mb-2 ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>No icons uploaded yet</h3>
              <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Upload your first icon to get started</p>
            </div>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-5">
              {[...icons]
                .filter(icon => !searchQuery || icon.name.toLowerCase().includes(searchQuery.toLowerCase()))
                .sort((a, b) => {
                  const aAssigned = (Array.isArray(a.assigned_to) ? a.assigned_to : (a.assigned_to ? [a.assigned_to] : [])).length;
                  const bAssigned = (Array.isArray(b.assigned_to) ? b.assigned_to : (b.assigned_to ? [b.assigned_to] : [])).length;
                  return (bAssigned > 0 ? 1 : 0) - (aAssigned > 0 ? 1 : 0);
                })
                .map(icon => (
                <div
                  key={icon.id}
                  onClick={selectMode ? () => toggleSelectIcon(icon.id) : undefined}
                  className={`rounded-xl p-4 border transition-all hover:shadow-lg ${
                    selectMode ? 'cursor-pointer' : ''
                  } ${
                    selectedIcons.has(icon.id)
                      ? 'border-red-500 ring-2 ring-red-300'
                      : isDarkMode ? 'bg-gray-800 border-gray-700 hover:border-gray-500' : 'bg-white border-gray-200 hover:border-blue-300'
                  }`}
                >
                  {/* Select Checkbox */}
                  {selectMode && (
                    <div className="flex justify-end mb-1">
                      <div className={`w-5 h-5 rounded border-2 flex items-center justify-center ${
                        selectedIcons.has(icon.id)
                          ? 'bg-red-500 border-red-500 text-white'
                          : isDarkMode ? 'border-gray-500' : 'border-gray-300'
                      }`}>
                        {selectedIcons.has(icon.id) && <span className="text-xs">✓</span>}
                      </div>
                    </div>
                  )}

                  {/* Icon Preview — click to open detail panel */}
                  <div
                    className={`w-full aspect-square rounded-lg flex items-center justify-center mb-3 p-2 cursor-pointer group relative ${isDarkMode ? 'bg-gray-700' : 'bg-gray-50'}`}
                    onClick={!selectMode ? () => openDetailPanel(icon, 'icon') : undefined}
                    title="Click to view details"
                  >
                    <img
                      src={getIconUrl(icon)}
                      alt={icon.alt_text || icon.name}
                      className="w-full h-full object-contain"
                    />
                    {!selectMode && (
                      <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-40 transition-all rounded-lg flex items-center justify-center">
                        <span className="text-white text-xs font-medium opacity-0 group-hover:opacity-100 transition-opacity">Details</span>
                      </div>
                    )}
                  </div>

                  {/* Inline Editable Name */}
                  {editingNameId === icon.id ? (
                    <input
                      type="text"
                      value={editingNameValue}
                      onChange={(e) => setEditingNameValue(e.target.value)}
                      onBlur={() => saveInlineName(icon.id, 'icon')}
                      onKeyDown={(e) => { if (e.key === 'Enter') saveInlineName(icon.id, 'icon'); if (e.key === 'Escape') setEditingNameId(null); }}
                      autoFocus
                      className={`w-full text-sm font-medium mb-2 px-2 py-1 rounded border ${isDarkMode ? 'bg-gray-700 border-gray-500 text-white' : 'bg-white border-gray-300 text-gray-800'}`}
                    />
                  ) : (
                    <p
                      className={`text-sm font-medium truncate mb-2 cursor-pointer hover:underline ${isDarkMode ? 'text-white' : 'text-gray-800'}`}
                      onClick={() => startEditName(icon.id, icon.name)}
                      title="Click to rename"
                    >
                      {icon.name}
                    </p>
                  )}

                  {!selectMode && (
                    <>
                      {/* Assigned game badges */}
                      {(Array.isArray(icon.assigned_to) ? icon.assigned_to : (icon.assigned_to ? [icon.assigned_to] : [])).length > 0 && (
                        <div className="flex flex-wrap gap-1 mb-2">
                          {(Array.isArray(icon.assigned_to) ? icon.assigned_to : [icon.assigned_to]).map(gid => {
                            const game = games.find(g => g.id === gid);
                            return (
                              <div key={gid} className="flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-green-100 text-green-700 font-medium">
                                <span className="truncate max-w-[80px]">{game ? game.name : gid}</span>
                                <button
                                  onClick={() => handleUnassign(icon.id, gid)}
                                  className="text-red-500 hover:text-red-700 font-bold"
                                  title="Remove"
                                >x</button>
                              </div>
                            );
                          })}
                        </div>
                      )}

                      {/* Assign to Game Dropdown */}
                      <select
                        value=""
                        onChange={(e) => { if (e.target.value) handleAssign(icon.id, e.target.value); }}
                        className={`w-full text-xs rounded-lg px-2 py-1.5 mb-2 border ${
                          isDarkMode
                            ? 'bg-gray-700 border-gray-600 text-gray-200'
                            : 'bg-gray-50 border-gray-200 text-gray-700'
                        }`}
                      >
                        <option value="">+ Assign to game</option>
                        {games
                          .filter(game => {
                            const assigned = Array.isArray(icon.assigned_to) ? icon.assigned_to : (icon.assigned_to ? [icon.assigned_to] : []);
                            return !assigned.includes(game.id);
                          })
                          .map(game => (
                            <option key={game.id} value={game.id}>{game.name}</option>
                          ))
                        }
                      </select>

                      {/* Icon Size — Presets + Custom */}
                      <div className="flex gap-1 mb-2">
                        <select
                          value={ICON_SIZES.find(s => s.value === icon.icon_size) ? icon.icon_size : 'custom'}
                          onChange={(e) => {
                            if (e.target.value !== 'custom') handleSizeChange(icon.id, e.target.value);
                          }}
                          className={`flex-1 text-xs rounded-lg px-2 py-1.5 border ${
                            isDarkMode
                              ? 'bg-gray-700 border-gray-600 text-gray-200'
                              : 'bg-gray-50 border-gray-200 text-gray-700'
                          }`}
                        >
                          {ICON_SIZES.map(size => (
                            <option key={size.value} value={size.value}>
                              {size.label} ({size.px}px)
                            </option>
                          ))}
                          <option value="custom">Custom</option>
                        </select>
                        <input
                          type="number"
                          min="16"
                          max="256"
                          placeholder="px"
                          defaultValue={
                            icon.icon_size && !ICON_SIZES.find(s => s.value === icon.icon_size)
                              ? icon.icon_size
                              : ''
                          }
                          onBlur={(e) => {
                            if (e.target.value) handleCustomSize(icon.id, e.target.value);
                          }}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' && e.target.value) handleCustomSize(icon.id, e.target.value);
                          }}
                          className={`w-16 text-xs rounded-lg px-2 py-1.5 border text-center ${
                            isDarkMode
                              ? 'bg-gray-700 border-gray-600 text-gray-200 placeholder-gray-500'
                              : 'bg-gray-50 border-gray-200 text-gray-700 placeholder-gray-400'
                          }`}
                        />
                      </div>

                      {/* Copy Link */}
                      <button
                        onClick={() => {
                          const fullUrl = getIconUrl(icon);
                          navigator.clipboard.writeText(fullUrl);
                          showNotification('Icon URL copied!', 'success');
                        }}
                        className={`w-full text-xs py-1.5 rounded-lg mb-2 font-medium ${
                          isDarkMode ? 'bg-gray-700 text-blue-400 hover:bg-gray-600' : 'bg-blue-50 text-blue-600 hover:bg-blue-100'
                        }`}
                      >
                        Copy Link
                      </button>

                      {/* File info + Delete */}
                      <div className="flex items-center justify-between">
                        <span className={`text-xs ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                          {(icon.file_size / 1024).toFixed(0)} KB
                        </span>
                        {deleteConfirm === icon.id ? (
                          <div className="flex gap-1">
                            <button
                              onClick={() => handleDelete(icon.id)}
                              className="text-xs px-2 py-1 bg-red-600 text-white rounded"
                            >
                              Yes
                            </button>
                            <button
                              onClick={() => setDeleteConfirm(null)}
                              className={`text-xs px-2 py-1 rounded ${isDarkMode ? 'bg-gray-600 text-gray-200' : 'bg-gray-200 text-gray-600'}`}
                            >
                              No
                            </button>
                          </div>
                        ) : (
                          <button
                            onClick={() => setDeleteConfirm(icon.id)}
                            className="text-xs text-red-500 hover:text-red-400"
                          >
                            Delete
                          </button>
                        )}
                      </div>
                    </>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Avatars Tab */}
      {activeTab === 'avatars' && (
        <div className="space-y-4">
          {/* Action Bar */}
          <div className="flex items-center justify-between gap-2 flex-wrap">
            <div className="flex items-center gap-2">
              {avatarImages.length > 0 && (
                <button
                  onClick={() => { setAvatarSelectMode(!avatarSelectMode); setSelectedAvatarImages(new Set()); setAvatarBulkDeleteConfirm(false); }}
                  className={`px-3 py-2 rounded-lg font-medium text-sm ${
                    avatarSelectMode
                      ? 'bg-red-100 text-red-700 hover:bg-red-200'
                      : isDarkMode
                        ? 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                        : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  {avatarSelectMode ? 'Cancel Select' : 'Select'}
                </button>
              )}
              {avatarSelectMode && (
                <>
                  <button
                    onClick={selectAllAvatarImages}
                    className={`px-3 py-2 rounded-lg font-medium text-sm ${isDarkMode ? 'bg-gray-700 text-gray-300 hover:bg-gray-600' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
                  >
                    {selectedAvatarImages.size === avatarImages.length ? 'Deselect All' : 'Select All'}
                  </button>
                  {selectedAvatarImages.size > 0 && !avatarBulkDeleteConfirm && (
                    <button
                      onClick={() => setAvatarBulkDeleteConfirm(true)}
                      className="px-3 py-2 rounded-lg font-medium text-sm bg-red-600 text-white hover:bg-red-700"
                    >
                      Delete {selectedAvatarImages.size} Selected
                    </button>
                  )}
                  {avatarBulkDeleteConfirm && (
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-red-500 font-medium">Are you sure?</span>
                      <button
                        onClick={handleAvatarBulkDelete}
                        className="px-3 py-2 rounded-lg font-medium text-sm bg-red-600 text-white hover:bg-red-700"
                      >
                        Yes, Delete {selectedAvatarImages.size}
                      </button>
                      <button
                        onClick={() => setAvatarBulkDeleteConfirm(false)}
                        className={`px-3 py-2 rounded-lg font-medium text-sm ${isDarkMode ? 'bg-gray-700 text-gray-300' : 'bg-gray-200 text-gray-600'}`}
                      >
                        No
                      </button>
                    </div>
                  )}
                </>
              )}
            </div>
            <button
              onClick={() => setShowUploadModal(true)}
              className="px-4 py-2 bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white rounded-lg font-medium text-sm shadow-md"
            >
              + Upload Avatars
            </button>
          </div>

          {/* Search + Stats */}
          {avatarImages.length > 0 && (() => {
            const assignedCount = avatarImages.filter(i => {
              const a = Array.isArray(i.assigned_to) ? i.assigned_to : (i.assigned_to ? [i.assigned_to] : []);
              return a.length > 0;
            }).length;
            return (
              <div className="flex items-center gap-4 flex-wrap">
                <input
                  type="text"
                  value={avatarSearchQuery}
                  onChange={(e) => setAvatarSearchQuery(e.target.value)}
                  placeholder="Search avatars..."
                  className={`flex-1 min-w-[200px] px-3 py-2 rounded-lg border text-sm ${
                    isDarkMode
                      ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400'
                      : 'bg-white border-gray-200 text-gray-800 placeholder-gray-400'
                  }`}
                />
                <div className={`flex gap-3 text-xs font-medium ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                  <span>Total: {avatarImages.length}</span>
                  <span className="text-green-500">Assigned: {assignedCount}</span>
                  <span className="text-orange-500">Unassigned: {avatarImages.length - assignedCount}</span>
                </div>
              </div>
            );
          })()}

          {/* Avatar Images Grid */}
          {avatarLoading ? (
            <div className="text-center py-12">
              <div className="animate-spin inline-block w-10 h-10 border-4 border-blue-500 border-t-transparent rounded-full"></div>
            </div>
          ) : avatarImages.length === 0 ? (
            <div className={`rounded-xl p-12 text-center ${isDarkMode ? 'bg-gray-800 border border-gray-700' : 'bg-white border border-gray-200'}`}>
              <div className="text-5xl mb-4">👤</div>
              <h3 className={`text-lg font-bold mb-2 ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>No avatar images uploaded yet</h3>
              <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Upload your first avatar image to get started</p>
            </div>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-5">
              {[...avatarImages]
                .filter(img => !avatarSearchQuery || img.name.toLowerCase().includes(avatarSearchQuery.toLowerCase()))
                .sort((a, b) => {
                  const aAssigned = (Array.isArray(a.assigned_to) ? a.assigned_to : (a.assigned_to ? [a.assigned_to] : [])).length;
                  const bAssigned = (Array.isArray(b.assigned_to) ? b.assigned_to : (b.assigned_to ? [b.assigned_to] : [])).length;
                  return (bAssigned > 0 ? 1 : 0) - (aAssigned > 0 ? 1 : 0);
                })
                .map(img => (
                <div
                  key={img.id}
                  onClick={avatarSelectMode ? () => toggleSelectAvatarImage(img.id) : undefined}
                  className={`rounded-xl p-4 border transition-all hover:shadow-lg ${
                    avatarSelectMode ? 'cursor-pointer' : ''
                  } ${
                    selectedAvatarImages.has(img.id)
                      ? 'border-red-500 ring-2 ring-red-300'
                      : isDarkMode ? 'bg-gray-800 border-gray-700 hover:border-gray-500' : 'bg-white border-gray-200 hover:border-blue-300'
                  }`}
                >
                  {/* Select Checkbox */}
                  {avatarSelectMode && (
                    <div className="flex justify-end mb-1">
                      <div className={`w-5 h-5 rounded border-2 flex items-center justify-center ${
                        selectedAvatarImages.has(img.id)
                          ? 'bg-red-500 border-red-500 text-white'
                          : isDarkMode ? 'border-gray-500' : 'border-gray-300'
                      }`}>
                        {selectedAvatarImages.has(img.id) && <span className="text-xs">✓</span>}
                      </div>
                    </div>
                  )}

                  {/* Avatar Preview — click to open detail panel */}
                  <div
                    className={`w-full aspect-square rounded-lg flex items-center justify-center mb-3 p-2 cursor-pointer group relative ${isDarkMode ? 'bg-gray-700' : 'bg-gray-50'}`}
                    onClick={!avatarSelectMode ? () => openDetailPanel(img, 'avatar') : undefined}
                    title="Click to view details"
                  >
                    <img
                      src={getAvatarImageUrl(img)}
                      alt={img.alt_text || img.name}
                      className="w-full h-full object-contain"
                    />
                    {!avatarSelectMode && (
                      <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-40 transition-all rounded-lg flex items-center justify-center">
                        <span className="text-white text-xs font-medium opacity-0 group-hover:opacity-100 transition-opacity">Details</span>
                      </div>
                    )}
                  </div>

                  {/* Inline Editable Name */}
                  {editingNameId === img.id ? (
                    <input
                      type="text"
                      value={editingNameValue}
                      onChange={(e) => setEditingNameValue(e.target.value)}
                      onBlur={() => saveInlineName(img.id, 'avatar')}
                      onKeyDown={(e) => { if (e.key === 'Enter') saveInlineName(img.id, 'avatar'); if (e.key === 'Escape') setEditingNameId(null); }}
                      autoFocus
                      className={`w-full text-sm font-medium mb-2 px-2 py-1 rounded border ${isDarkMode ? 'bg-gray-700 border-gray-500 text-white' : 'bg-white border-gray-300 text-gray-800'}`}
                    />
                  ) : (
                    <p
                      className={`text-sm font-medium truncate mb-2 cursor-pointer hover:underline ${isDarkMode ? 'text-white' : 'text-gray-800'}`}
                      onClick={() => startEditName(img.id, img.name)}
                      title="Click to rename"
                    >
                      {img.name}
                    </p>
                  )}

                  {!avatarSelectMode && (
                    <>
                      {/* Assigned avatar badges */}
                      {(Array.isArray(img.assigned_to) ? img.assigned_to : (img.assigned_to ? [img.assigned_to] : [])).length > 0 && (
                        <div className="flex flex-wrap gap-1 mb-2">
                          {(Array.isArray(img.assigned_to) ? img.assigned_to : [img.assigned_to]).map(aid => {
                            const avatar = coreAvatars.find(a => a.id === aid);
                            return (
                              <div key={aid} className="flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-green-100 text-green-700 font-medium">
                                <span className="truncate max-w-[80px]">{avatar ? avatar.name : aid}</span>
                                <button
                                  onClick={() => handleAvatarUnassign(img.id, aid)}
                                  className="text-red-500 hover:text-red-700 font-bold"
                                  title="Remove"
                                >x</button>
                              </div>
                            );
                          })}
                        </div>
                      )}

                      {/* Assign to Avatar Dropdown */}
                      <select
                        value=""
                        onChange={(e) => { if (e.target.value) handleAvatarAssign(img.id, e.target.value); }}
                        className={`w-full text-xs rounded-lg px-2 py-1.5 mb-2 border ${
                          isDarkMode
                            ? 'bg-gray-700 border-gray-600 text-gray-200'
                            : 'bg-gray-50 border-gray-200 text-gray-700'
                        }`}
                      >
                        <option value="">+ Assign to avatar</option>
                        {coreAvatars
                          .filter(avatar => {
                            const assigned = Array.isArray(img.assigned_to) ? img.assigned_to : (img.assigned_to ? [img.assigned_to] : []);
                            return !assigned.includes(avatar.id);
                          })
                          .map(avatar => (
                            <option key={avatar.id} value={avatar.id}>{avatar.name} ({avatar.gender || 'unisex'})</option>
                          ))
                        }
                      </select>

                      {/* Avatar Size — Presets + Custom */}
                      <div className="flex gap-1 mb-2">
                        <select
                          value={ICON_SIZES.find(s => s.value === img.avatar_size) ? img.avatar_size : 'custom'}
                          onChange={(e) => {
                            if (e.target.value !== 'custom') handleAvatarSizeChange(img.id, e.target.value);
                          }}
                          className={`flex-1 text-xs rounded-lg px-2 py-1.5 border ${
                            isDarkMode
                              ? 'bg-gray-700 border-gray-600 text-gray-200'
                              : 'bg-gray-50 border-gray-200 text-gray-700'
                          }`}
                        >
                          {ICON_SIZES.map(size => (
                            <option key={size.value} value={size.value}>
                              {size.label} ({size.px}px)
                            </option>
                          ))}
                          <option value="custom">Custom</option>
                        </select>
                        <input
                          type="number"
                          min="16"
                          max="256"
                          placeholder="px"
                          defaultValue={
                            img.avatar_size && !ICON_SIZES.find(s => s.value === img.avatar_size)
                              ? img.avatar_size
                              : ''
                          }
                          onBlur={(e) => {
                            if (e.target.value) handleAvatarCustomSize(img.id, e.target.value);
                          }}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' && e.target.value) handleAvatarCustomSize(img.id, e.target.value);
                          }}
                          className={`w-16 text-xs rounded-lg px-2 py-1.5 border text-center ${
                            isDarkMode
                              ? 'bg-gray-700 border-gray-600 text-gray-200 placeholder-gray-500'
                              : 'bg-gray-50 border-gray-200 text-gray-700 placeholder-gray-400'
                          }`}
                        />
                      </div>

                      {/* Copy Link */}
                      <button
                        onClick={() => {
                          const fullUrl = getAvatarImageUrl(img);
                          navigator.clipboard.writeText(fullUrl);
                          showNotification('Avatar URL copied!', 'success');
                        }}
                        className={`w-full text-xs py-1.5 rounded-lg mb-2 font-medium ${
                          isDarkMode ? 'bg-gray-700 text-blue-400 hover:bg-gray-600' : 'bg-blue-50 text-blue-600 hover:bg-blue-100'
                        }`}
                      >
                        Copy Link
                      </button>

                      {/* File info + Delete */}
                      <div className="flex items-center justify-between">
                        <span className={`text-xs ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                          {(img.file_size / 1024).toFixed(0)} KB
                        </span>
                        {avatarDeleteConfirm === img.id ? (
                          <div className="flex gap-1">
                            <button
                              onClick={() => handleAvatarDelete(img.id)}
                              className="text-xs px-2 py-1 bg-red-600 text-white rounded"
                            >
                              Yes
                            </button>
                            <button
                              onClick={() => setAvatarDeleteConfirm(null)}
                              className={`text-xs px-2 py-1 rounded ${isDarkMode ? 'bg-gray-600 text-gray-200' : 'bg-gray-200 text-gray-600'}`}
                            >
                              No
                            </button>
                          </div>
                        ) : (
                          <button
                            onClick={() => setAvatarDeleteConfirm(img.id)}
                            className="text-xs text-red-500 hover:text-red-400"
                          >
                            Delete
                          </button>
                        )}
                      </div>
                    </>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Dynamic Folder Tab Content */}
      {activeFolderTab && (() => {
        const activeFolder = folders.find(f => f.id === activeFolderTab);
        return (
          <div className="space-y-4">
            <div className="flex items-center justify-between gap-2 flex-wrap">
              <div className="flex items-center gap-3">
                <h3 className={`text-lg font-bold ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
                  {activeFolder?.icon} {activeFolder?.name}
                </h3>
                {folderDeleteConfirm === activeFolderTab ? (
                  <div className="flex gap-1">
                    <button onClick={() => { deleteFolder(activeFolderTab); setActiveTab('icons'); }} className="text-xs px-2 py-1 bg-red-600 text-white rounded">Delete Folder</button>
                    <button onClick={() => setFolderDeleteConfirm(null)} className={`text-xs px-2 py-1 rounded ${isDarkMode ? 'bg-gray-600 text-gray-200' : 'bg-gray-200 text-gray-600'}`}>Cancel</button>
                  </div>
                ) : (
                  <button
                    onClick={() => setFolderDeleteConfirm(activeFolderTab)}
                    className="text-xs text-red-500 hover:text-red-400"
                  >
                    Delete Folder
                  </button>
                )}
              </div>
              <div className="flex items-center gap-2">
                {folderAssets.length > 0 && (
                  <button
                    onClick={() => { setFolderSelectMode(!folderSelectMode); setSelectedAssets(new Set()); setFolderBulkDeleteConfirm(false); }}
                    className={`px-3 py-2 rounded-lg font-medium text-sm ${
                      folderSelectMode
                        ? 'bg-red-100 text-red-700 hover:bg-red-200'
                        : isDarkMode ? 'bg-gray-700 text-gray-300 hover:bg-gray-600' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                    }`}
                  >
                    {folderSelectMode ? 'Cancel Select' : 'Select'}
                  </button>
                )}
                {folderSelectMode && selectedAssets.size > 0 && !folderBulkDeleteConfirm && (
                  <button onClick={() => setFolderBulkDeleteConfirm(true)} className="px-3 py-2 rounded-lg font-medium text-sm bg-red-600 text-white hover:bg-red-700">
                    Delete {selectedAssets.size} Selected
                  </button>
                )}
                {folderBulkDeleteConfirm && (
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-red-500 font-medium">Are you sure?</span>
                    <button onClick={handleAssetBulkDelete} className="px-3 py-2 rounded-lg font-medium text-sm bg-red-600 text-white hover:bg-red-700">Yes, Delete</button>
                    <button onClick={() => setFolderBulkDeleteConfirm(false)} className={`px-3 py-2 rounded-lg font-medium text-sm ${isDarkMode ? 'bg-gray-700 text-gray-300' : 'bg-gray-200 text-gray-600'}`}>No</button>
                  </div>
                )}
                <button
                  onClick={() => setShowUploadModal(true)}
                  className="px-4 py-2 bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white rounded-lg font-medium text-sm shadow-md"
                >
                  + Upload Assets
                </button>
              </div>
            </div>

            {/* Search */}
            {folderAssets.length > 0 && (
              <div className="flex items-center gap-4 flex-wrap">
                <input
                  type="text"
                  value={folderSearchQuery}
                  onChange={(e) => setFolderSearchQuery(e.target.value)}
                  placeholder="Search assets..."
                  className={`flex-1 min-w-[200px] px-3 py-2 rounded-lg border text-sm ${
                    isDarkMode ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400' : 'bg-white border-gray-200 text-gray-800 placeholder-gray-400'
                  }`}
                />
                <span className={`text-xs font-medium ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                  Total: {folderAssets.length}
                </span>
              </div>
            )}

            {/* Assets Grid */}
            {folderAssetsLoading ? (
              <div className="text-center py-12">
                <div className="animate-spin inline-block w-10 h-10 border-4 border-blue-500 border-t-transparent rounded-full"></div>
              </div>
            ) : folderAssets.length === 0 ? (
              <div className={`rounded-xl p-12 text-center ${isDarkMode ? 'bg-gray-800 border border-gray-700' : 'bg-white border border-gray-200'}`}>
                <div className="text-5xl mb-4">🖼️</div>
                <h3 className={`text-lg font-bold mb-2 ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>No assets yet</h3>
                <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Upload your first image to this folder</p>
              </div>
            ) : (
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-5">
                {folderAssets
                  .filter(a => !folderSearchQuery || a.name.toLowerCase().includes(folderSearchQuery.toLowerCase()))
                  .map(asset => (
                  <div
                    key={asset.id}
                    onClick={folderSelectMode ? () => setSelectedAssets(prev => {
                      const next = new Set(prev);
                      if (next.has(asset.id)) next.delete(asset.id); else next.add(asset.id);
                      return next;
                    }) : undefined}
                    className={`rounded-xl p-4 border transition-all hover:shadow-lg ${
                      folderSelectMode ? 'cursor-pointer' : ''
                    } ${
                      selectedAssets.has(asset.id)
                        ? 'border-red-500 ring-2 ring-red-300'
                        : isDarkMode ? 'bg-gray-800 border-gray-700 hover:border-gray-500' : 'bg-white border-gray-200 hover:border-blue-300'
                    }`}
                  >
                    {folderSelectMode && (
                      <div className="flex justify-end mb-1">
                        <div className={`w-5 h-5 rounded border-2 flex items-center justify-center ${
                          selectedAssets.has(asset.id) ? 'bg-red-500 border-red-500 text-white' : isDarkMode ? 'border-gray-500' : 'border-gray-300'
                        }`}>
                          {selectedAssets.has(asset.id) && <span className="text-xs">✓</span>}
                        </div>
                      </div>
                    )}

                    <div
                      className={`w-full aspect-square rounded-lg flex items-center justify-center mb-3 p-2 cursor-pointer group relative ${isDarkMode ? 'bg-gray-700' : 'bg-gray-50'}`}
                      onClick={!folderSelectMode ? () => openDetailPanel(asset, 'asset') : undefined}
                    >
                      <img src={getImageUrl(asset)} alt={asset.alt_text || asset.name} className="w-full h-full object-contain" />
                      {!folderSelectMode && (
                        <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-40 transition-all rounded-lg flex items-center justify-center">
                          <span className="text-white text-xs font-medium opacity-0 group-hover:opacity-100 transition-opacity">Details</span>
                        </div>
                      )}
                    </div>

                    {editingNameId === asset.id ? (
                      <input
                        type="text"
                        value={editingNameValue}
                        onChange={(e) => setEditingNameValue(e.target.value)}
                        onBlur={() => saveInlineName(asset.id, 'asset')}
                        onKeyDown={(e) => { if (e.key === 'Enter') saveInlineName(asset.id, 'asset'); if (e.key === 'Escape') setEditingNameId(null); }}
                        autoFocus
                        className={`w-full text-sm font-medium mb-2 px-2 py-1 rounded border ${isDarkMode ? 'bg-gray-700 border-gray-500 text-white' : 'bg-white border-gray-300 text-gray-800'}`}
                      />
                    ) : (
                      <p
                        className={`text-sm font-medium truncate mb-2 cursor-pointer hover:underline ${isDarkMode ? 'text-white' : 'text-gray-800'}`}
                        onClick={() => startEditName(asset.id, asset.name)}
                        title="Click to rename"
                      >
                        {asset.name}
                      </p>
                    )}

                    {!folderSelectMode && (
                      <>
                        <button
                          onClick={() => { navigator.clipboard.writeText(getImageUrl(asset)); showNotification('URL copied!', 'success'); }}
                          className={`w-full text-xs py-1.5 rounded-lg mb-2 font-medium ${isDarkMode ? 'bg-gray-700 text-blue-400 hover:bg-gray-600' : 'bg-blue-50 text-blue-600 hover:bg-blue-100'}`}
                        >
                          Copy Link
                        </button>
                        <div className="flex items-center justify-between">
                          <span className={`text-xs ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                            {(asset.file_size / 1024).toFixed(0)} KB
                          </span>
                          {assetDeleteConfirm === asset.id ? (
                            <div className="flex gap-1">
                              <button onClick={() => handleAssetDelete(asset.id)} className="text-xs px-2 py-1 bg-red-600 text-white rounded">Yes</button>
                              <button onClick={() => setAssetDeleteConfirm(null)} className={`text-xs px-2 py-1 rounded ${isDarkMode ? 'bg-gray-600 text-gray-200' : 'bg-gray-200 text-gray-600'}`}>No</button>
                            </div>
                          ) : (
                            <button onClick={() => setAssetDeleteConfirm(asset.id)} className="text-xs text-red-500 hover:text-red-400">Delete</button>
                          )}
                        </div>
                      </>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        );
      })()}

      {/* Settings Tab */}
      {activeTab === 'settings' && (
        <div className={`rounded-xl p-6 ${isDarkMode ? 'bg-gray-800 border border-gray-700' : 'bg-white border border-gray-200'}`}>
          <h3 className={`text-lg font-bold mb-4 ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>⚙️ Image Manager Settings</h3>

          <div className="space-y-4">
            <div>
              <label className={`text-sm font-semibold ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                Max Upload Size
              </label>
              <div className="flex items-center gap-3 mt-1">
                <input type="range" min="1" max="50" step="1"
                  value={imgSettings.max_upload_mb || 10}
                  onChange={e => setImgSettings(prev => ({ ...prev, max_upload_mb: parseInt(e.target.value) }))}
                  className="flex-1 h-2 rounded-lg appearance-none cursor-pointer"
                  style={{ accentColor: '#3b82f6' }} />
                <span className={`text-sm font-mono font-bold w-16 text-center ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
                  {imgSettings.max_upload_mb || 10} MB
                </span>
              </div>
              <p className={`text-xs mt-1 ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                Maximum file size for all image uploads (icons, avatars, assets). For high-res backgrounds use 10MB+.
              </p>
              <button onClick={() => saveImageSettings({ max_upload_mb: imgSettings.max_upload_mb })}
                className="mt-2 bg-blue-500 hover:bg-blue-600 text-white px-4 py-1.5 rounded-lg text-xs font-semibold">
                Save
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Optimize Tab — Coming Soon */}
      {activeTab === 'optimize' && (
        <div className={`rounded-xl p-12 text-center ${isDarkMode ? 'bg-gray-800 border border-gray-700' : 'bg-white border border-gray-200'}`}>
          <div className="text-5xl mb-4">⚡</div>
          <h3 className={`text-lg font-bold mb-2 ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>Optimize — Coming Soon</h3>
          <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>This feature will be available soon</p>
        </div>
      )}

      {/* Upload Modal */}
      {showUploadModal && !showCropModal && (
        <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50 p-4">
          <div className={`rounded-xl p-6 max-w-md w-full ${isDarkMode ? 'bg-gray-800' : 'bg-white'} shadow-2xl`}>
            <h3 className={`text-xl font-bold mb-4 ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
              Upload {activeTab === 'avatars' ? 'Avatars' : activeFolderTab ? 'Assets' : 'Icons'}
            </h3>

            {/* Mode Toggle */}
            <div className="flex gap-2 mb-4">
              <button
                onClick={() => { setUploadMode('single'); setBulkFiles([]); }}
                className={`flex-1 py-2 rounded-lg text-sm font-medium ${
                  uploadMode === 'single'
                    ? 'bg-blue-600 text-white'
                    : isDarkMode ? 'bg-gray-700 text-gray-300' : 'bg-gray-100 text-gray-600'
                }`}
              >
                Single
              </button>
              <button
                onClick={() => { setUploadMode('bulk'); setUploadFile(null); setUploadPreview(null); setUploadName(''); }}
                className={`flex-1 py-2 rounded-lg text-sm font-medium ${
                  uploadMode === 'bulk'
                    ? 'bg-blue-600 text-white'
                    : isDarkMode ? 'bg-gray-700 text-gray-300' : 'bg-gray-100 text-gray-600'
                }`}
              >
                Bulk Upload
              </button>
            </div>

            {/* Single Upload */}
            {uploadMode === 'single' && (
              <div
                onClick={() => (activeTab === 'avatars' ? avatarFileInputRef : activeFolderTab ? folderFileInputRef : fileInputRef).current?.click()}
                className={`border-2 border-dashed rounded-xl p-8 text-center cursor-pointer transition-colors mb-4 ${
                  isDarkMode
                    ? 'border-gray-600 hover:border-blue-500 bg-gray-700'
                    : 'border-gray-300 hover:border-blue-400 bg-gray-50'
                }`}
              >
                <div className="text-4xl mb-2">{activeTab === 'avatars' ? '👤' : activeFolderTab ? '🖼️' : '📁'}</div>
                <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                  Click to select PNG, JPEG, or WebP (max {imgSettings.max_upload_mb || 10}MB)
                </p>
                <p className={`text-xs mt-1 ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                  Crop tool will open after selection
                </p>
                <input ref={fileInputRef} type="file" accept="image/png,image/jpeg,image/webp" onChange={handleFileSelect} className="hidden" />
                <input ref={avatarFileInputRef} type="file" accept="image/png,image/jpeg,image/webp" onChange={handleAvatarFileSelect} className="hidden" />
                <input ref={folderFileInputRef} type="file" accept="image/png,image/jpeg,image/webp" onChange={handleFolderAssetFileSelect} className="hidden" />
              </div>
            )}

            {/* Bulk Upload */}
            {uploadMode === 'bulk' && (
              <>
                <div
                  onClick={() => (activeTab === 'avatars' ? avatarBulkFileInputRef : activeFolderTab ? folderBulkFileInputRef : bulkFileInputRef).current?.click()}
                  className={`border-2 border-dashed rounded-xl p-8 text-center cursor-pointer transition-colors mb-4 ${
                    isDarkMode
                      ? 'border-gray-600 hover:border-blue-500 bg-gray-700'
                      : 'border-gray-300 hover:border-blue-400 bg-gray-50'
                  }`}
                >
                  {bulkFiles.length > 0 ? (
                    <div>
                      <div className="text-4xl mb-2">📦</div>
                      <p className={`text-sm font-medium ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
                        {bulkFiles.length} file(s) selected
                      </p>
                      <p className={`text-xs mt-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                        Click to change selection
                      </p>
                    </div>
                  ) : (
                    <div>
                      <div className="text-4xl mb-2">📦</div>
                      <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                        Click to select multiple files
                      </p>
                      <p className={`text-xs mt-1 ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                        PNG, JPEG, or WebP (max {imgSettings.max_upload_mb || 10}MB each). Names auto-generated.
                      </p>
                    </div>
                  )}
                  <input
                    ref={bulkFileInputRef}
                    type="file"
                    accept="image/png,image/jpeg,image/webp"
                    multiple
                    onChange={handleBulkFileSelect}
                    className="hidden"
                  />
                  <input
                    ref={avatarBulkFileInputRef}
                    type="file"
                    accept="image/png,image/jpeg,image/webp"
                    multiple
                    onChange={handleAvatarBulkFileSelect}
                    className="hidden"
                  />
                  <input
                    ref={folderBulkFileInputRef}
                    type="file"
                    accept="image/png,image/jpeg,image/webp"
                    multiple
                    onChange={handleBulkFileSelect}
                    className="hidden"
                  />
                </div>

                {bulkFiles.length > 0 && (
                  <div className={`max-h-32 overflow-y-auto rounded-lg p-2 mb-4 text-xs ${isDarkMode ? 'bg-gray-700 text-gray-300' : 'bg-gray-50 text-gray-600'}`}>
                    {bulkFiles.map((f, i) => (
                      <div key={i} className="py-0.5 truncate">{f.name} ({(f.size / 1024).toFixed(0)} KB)</div>
                    ))}
                  </div>
                )}
              </>
            )}

            {/* Buttons */}
            <div className="flex gap-3">
              <button
                onClick={closeUploadModal}
                className={`flex-1 py-2 rounded-lg font-medium text-sm ${
                  isDarkMode ? 'bg-gray-700 hover:bg-gray-600 text-white' : 'bg-gray-200 hover:bg-gray-300 text-gray-700'
                }`}
              >
                Cancel
              </button>
              {uploadMode === 'bulk' && (
                <button
                  onClick={activeTab === 'avatars' ? handleAvatarBulkUpload : activeFolderTab ? handleFolderBulkUpload : handleBulkUpload}
                  disabled={uploading || bulkFiles.length === 0}
                  className="flex-1 py-2 bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white rounded-lg font-medium text-sm disabled:opacity-50"
                >
                  {uploading ? 'Uploading...' : `Upload ${bulkFiles.length} Files`}
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Crop Modal — works for both new uploads and existing icons */}
      {showCropModal && uploadPreview && (
        <div className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center z-50 p-4">
          <div className={`rounded-xl p-6 max-w-4xl w-full ${isDarkMode ? 'bg-gray-800' : 'bg-white'} shadow-2xl`} style={{ maxHeight: '95vh', display: 'flex', flexDirection: 'column' }}>
            <h3 className={`text-lg font-bold mb-2 ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
              {cropMode === 'existing' ? `Crop ${cropTarget === 'avatar' ? 'Avatar' : cropTarget === 'asset' ? 'Asset' : 'Icon'}` : 'Crop & Upload'}
            </h3>
            <p className={`text-sm mb-3 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
              Drag to select the area you want to keep.
              {cropMode === 'existing' && ' Saves as new icon replacing the old one.'}
            </p>

            {/* Icon Name Input */}
            <div className="mb-3">
              <label className={`block text-sm font-medium mb-1 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                Icon Name
              </label>
              <input
                type="text"
                value={uploadName}
                onChange={(e) => setUploadName(e.target.value)}
                placeholder="e.g. conversation-game"
                className={`w-full px-3 py-2 rounded-lg border text-sm ${
                  isDarkMode
                    ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400'
                    : 'bg-white border-gray-300 text-gray-800 placeholder-gray-400'
                }`}
              />
            </div>

            {/* Crop Area */}
            <div className={`rounded-lg overflow-auto mb-4 flex-1 flex items-center justify-center ${isDarkMode ? 'bg-gray-900' : 'bg-gray-100'}`} style={{ minHeight: '300px', maxHeight: '60vh' }}>
              <ReactCrop
                crop={crop}
                onChange={(c) => setCrop(c)}
                onComplete={(c) => setCompletedCrop(c)}
              >
                <img
                  src={uploadPreview}
                  alt="Crop preview"
                  onLoad={onImageLoad}
                  crossOrigin="anonymous"
                  style={{ maxWidth: '100%', maxHeight: '55vh', display: 'block', margin: '0 auto' }}
                />
              </ReactCrop>
            </div>

            {/* Buttons */}
            <div className="flex gap-3">
              <button
                onClick={() => { closeCropModal(); if (cropMode === 'new') { setUploadFile(null); setUploadPreview(null); } }}
                className={`py-2 px-4 rounded-lg font-medium text-sm ${
                  isDarkMode ? 'bg-gray-700 hover:bg-gray-600 text-white' : 'bg-gray-200 hover:bg-gray-300 text-gray-700'
                }`}
              >
                Cancel
              </button>
              {cropMode === 'new' && (
                <button
                  onClick={handleUploadOriginal}
                  disabled={uploading}
                  className={`py-2 px-4 rounded-lg font-medium text-sm ${
                    isDarkMode ? 'bg-gray-600 hover:bg-gray-500 text-gray-200' : 'bg-gray-100 hover:bg-gray-200 text-gray-700'
                  } disabled:opacity-50`}
                >
                  Skip Crop
                </button>
              )}
              <button
                onClick={handleCropAndUpload}
                disabled={uploading || !completedCrop}
                className="flex-1 py-2 bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white rounded-lg font-medium text-sm disabled:opacity-50"
              >
                {uploading ? 'Saving...' : cropMode === 'existing' ? 'Crop & Save' : 'Crop & Upload'}
              </button>
            </div>
          </div>
        </div>
      )}
      {/* Create Folder Modal */}
      {showCreateFolderModal && (
        <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50 p-4">
          <div className={`rounded-xl p-6 max-w-sm w-full ${isDarkMode ? 'bg-gray-800' : 'bg-white'} shadow-2xl`}>
            <h3 className={`text-lg font-bold mb-4 ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>Create Folder</h3>
            <div className="mb-4">
              <label className={`block text-sm font-medium mb-1 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>Folder Name</label>
              <input
                type="text"
                value={newFolderName}
                onChange={(e) => setNewFolderName(e.target.value)}
                placeholder="e.g. Daily Words Icons"
                autoFocus
                onKeyDown={(e) => { if (e.key === 'Enter') createFolder(); }}
                className={`w-full px-3 py-2 rounded-lg border text-sm ${isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300 text-gray-800'}`}
              />
            </div>
            <div className="mb-4">
              <label className={`block text-sm font-medium mb-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>Icon</label>
              <div className="flex flex-wrap gap-2">
                {FOLDER_EMOJIS.map(emoji => (
                  <button
                    key={emoji}
                    onClick={() => setNewFolderIcon(emoji)}
                    className={`w-10 h-10 rounded-lg text-xl flex items-center justify-center transition-all ${
                      newFolderIcon === emoji
                        ? 'bg-blue-100 border-2 border-blue-500 scale-110'
                        : isDarkMode ? 'bg-gray-700 hover:bg-gray-600' : 'bg-gray-100 hover:bg-gray-200'
                    }`}
                  >
                    {emoji}
                  </button>
                ))}
              </div>
            </div>
            <div className="flex gap-3">
              <button
                onClick={() => { setShowCreateFolderModal(false); setNewFolderName(''); setNewFolderIcon('📁'); }}
                className={`flex-1 py-2 rounded-lg font-medium text-sm ${isDarkMode ? 'bg-gray-700 hover:bg-gray-600 text-white' : 'bg-gray-200 hover:bg-gray-300 text-gray-700'}`}
              >
                Cancel
              </button>
              <button
                onClick={createFolder}
                className="flex-1 py-2 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-lg font-medium text-sm"
              >
                Create
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Detail Panel Modal (WordPress-style) */}
      {detailImage && (
        <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50 p-4">
          <div className={`rounded-xl p-6 max-w-3xl w-full ${isDarkMode ? 'bg-gray-800' : 'bg-white'} shadow-2xl`} style={{ maxHeight: '90vh', overflowY: 'auto' }}>
            <div className="flex items-start gap-6">
              {/* Image Preview */}
              <div className={`w-64 h-64 flex-shrink-0 rounded-lg flex items-center justify-center p-3 ${isDarkMode ? 'bg-gray-700' : 'bg-gray-100'}`}>
                <img
                  src={getImageUrl(detailImage)}
                  alt={detailImage.alt_text || detailImage.name}
                  className="max-w-full max-h-full object-contain"
                />
              </div>

              {/* Metadata Fields */}
              <div className="flex-1 space-y-3">
                <div>
                  <label className={`block text-xs font-medium mb-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Name</label>
                  <input
                    type="text"
                    value={detailEditing.name || ''}
                    onChange={(e) => setDetailEditing(prev => ({ ...prev, name: e.target.value }))}
                    onBlur={() => saveDetailField('name', detailEditing.name)}
                    className={`w-full px-3 py-2 rounded-lg border text-sm ${isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300 text-gray-800'}`}
                  />
                </div>
                <div>
                  <label className={`block text-xs font-medium mb-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Alt Text</label>
                  <input
                    type="text"
                    value={detailEditing.alt_text || ''}
                    onChange={(e) => setDetailEditing(prev => ({ ...prev, alt_text: e.target.value }))}
                    onBlur={() => saveDetailField('alt_text', detailEditing.alt_text)}
                    placeholder="Describe the image for accessibility"
                    className={`w-full px-3 py-2 rounded-lg border text-sm ${isDarkMode ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-500' : 'bg-white border-gray-300 text-gray-800 placeholder-gray-400'}`}
                  />
                </div>
                <div>
                  <label className={`block text-xs font-medium mb-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Description</label>
                  <textarea
                    value={detailEditing.description || ''}
                    onChange={(e) => setDetailEditing(prev => ({ ...prev, description: e.target.value }))}
                    onBlur={() => saveDetailField('description', detailEditing.description)}
                    placeholder="Optional description"
                    rows={2}
                    className={`w-full px-3 py-2 rounded-lg border text-sm ${isDarkMode ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-500' : 'bg-white border-gray-300 text-gray-800 placeholder-gray-400'}`}
                  />
                </div>

                {/* Info row */}
                <div className={`flex flex-wrap gap-4 text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                  {detailImage.uploaded_at && (
                    <span>Uploaded: {new Date(detailImage.uploaded_at).toLocaleDateString()}</span>
                  )}
                  {detailImage.created_at && !detailImage.uploaded_at && (
                    <span>Created: {new Date(detailImage.created_at).toLocaleDateString()}</span>
                  )}
                  {detailImage.file_size && (
                    <span>Size: {(detailImage.file_size / 1024).toFixed(0)} KB</span>
                  )}
                  {detailImage.dimensions && detailImage.dimensions.width > 0 && (
                    <span>Dimensions: {detailImage.dimensions.width} x {detailImage.dimensions.height}</span>
                  )}
                </div>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex flex-wrap gap-2 mt-5 pt-4 border-t border-gray-200 dark:border-gray-700">
              <button
                onClick={handleDetailCrop}
                className={`px-4 py-2 rounded-lg text-sm font-medium ${isDarkMode ? 'bg-gray-700 text-gray-200 hover:bg-gray-600' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}
              >
                Crop
              </button>
              <button
                onClick={openWatermark}
                className={`px-4 py-2 rounded-lg text-sm font-medium ${isDarkMode ? 'bg-gray-700 text-gray-200 hover:bg-gray-600' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}
              >
                Watermark
              </button>
              <button
                onClick={handleDetailDownload}
                className={`px-4 py-2 rounded-lg text-sm font-medium ${isDarkMode ? 'bg-gray-700 text-gray-200 hover:bg-gray-600' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}
              >
                Download
              </button>
              <button
                onClick={() => { navigator.clipboard.writeText(getImageUrl(detailImage)); showNotification('URL copied!', 'success'); }}
                className={`px-4 py-2 rounded-lg text-sm font-medium ${isDarkMode ? 'bg-gray-700 text-blue-400 hover:bg-gray-600' : 'bg-blue-50 text-blue-600 hover:bg-blue-100'}`}
              >
                Copy Link
              </button>
              <button
                onClick={() => setShowMoveModal(true)}
                className={`px-4 py-2 rounded-lg text-sm font-medium ${isDarkMode ? 'bg-gray-700 text-yellow-400 hover:bg-gray-600' : 'bg-yellow-50 text-yellow-700 hover:bg-yellow-100'}`}
              >
                Move to
              </button>
              <div className="flex-1"></div>
              <button
                onClick={handleDetailDelete}
                className="px-4 py-2 rounded-lg text-sm font-medium bg-red-600 text-white hover:bg-red-700"
              >
                Delete
              </button>
              <button
                onClick={closeDetailPanel}
                className={`px-4 py-2 rounded-lg text-sm font-medium ${isDarkMode ? 'bg-gray-600 text-white hover:bg-gray-500' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'}`}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Move Image Modal */}
      {showMoveModal && detailImage && (
        <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-[60] p-4">
          <div className={`rounded-xl p-6 max-w-sm w-full ${isDarkMode ? 'bg-gray-800' : 'bg-white'} shadow-2xl`}>
            <h3 className={`text-lg font-bold mb-4 ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>Move to...</h3>
            <p className={`text-sm mb-4 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
              Moving: <strong>{detailImage.name || detailImage.filename}</strong>
            </p>
            <div className="flex flex-col gap-2 max-h-64 overflow-y-auto">
              {/* Icons tab */}
              {detailType !== 'icon' && (
                <button
                  onClick={() => handleMoveImage('icon')}
                  disabled={movingImage}
                  className={`text-left px-4 py-3 rounded-lg text-sm font-medium ${isDarkMode ? 'bg-gray-700 text-gray-200 hover:bg-gray-600' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}
                >
                  Icons
                </button>
              )}
              {/* Avatars tab */}
              {detailType !== 'avatar' && (
                <button
                  onClick={() => handleMoveImage('avatar')}
                  disabled={movingImage}
                  className={`text-left px-4 py-3 rounded-lg text-sm font-medium ${isDarkMode ? 'bg-gray-700 text-gray-200 hover:bg-gray-600' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}
                >
                  Avatars
                </button>
              )}
              {/* Folder tabs */}
              {folders.filter(f => !(detailType === 'asset' && detailImage.folder_id === f.id)).map(f => (
                <button
                  key={f.id}
                  onClick={() => handleMoveImage('folder', f.id)}
                  disabled={movingImage}
                  className={`text-left px-4 py-3 rounded-lg text-sm font-medium ${isDarkMode ? 'bg-gray-700 text-gray-200 hover:bg-gray-600' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}
                >
                  {f.icon || '📁'} {f.name}
                </button>
              ))}
              {folders.length === 0 && detailType !== 'icon' && detailType !== 'avatar' && (
                <p className={`text-sm italic ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>No other destinations available</p>
              )}
            </div>
            <div className="flex justify-end mt-4 gap-2">
              <button
                onClick={() => setShowMoveModal(false)}
                disabled={movingImage}
                className={`px-4 py-2 rounded-lg text-sm font-medium ${isDarkMode ? 'bg-gray-600 text-white hover:bg-gray-500' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'}`}
              >
                Cancel
              </button>
            </div>
            {movingImage && (
              <p className={`text-sm mt-2 text-center ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Moving...</p>
            )}
          </div>
        </div>
      )}

      {/* Watermark Modal */}
      {showWatermarkModal && detailImage && (
        <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-[60] p-4">
          <div className={`rounded-xl p-6 max-w-md w-full ${isDarkMode ? 'bg-gray-800' : 'bg-white'} shadow-2xl`}>
            <h3 className={`text-lg font-bold mb-4 ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>Apply Watermark</h3>

            {/* Watermark Image */}
            <div className="mb-4">
              <label className={`block text-sm font-medium mb-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>Watermark Image</label>
              <div
                onClick={() => watermarkFileRef.current?.click()}
                className={`border-2 border-dashed rounded-lg p-4 text-center cursor-pointer ${
                  isDarkMode ? 'border-gray-600 hover:border-blue-500 bg-gray-700' : 'border-gray-300 hover:border-blue-400 bg-gray-50'
                }`}
              >
                {watermarkImage ? (
                  <p className={`text-sm ${isDarkMode ? 'text-green-400' : 'text-green-600'}`}>{watermarkImage.name}</p>
                ) : (
                  <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Click to select watermark image</p>
                )}
                <input
                  ref={watermarkFileRef}
                  type="file"
                  accept="image/png,image/jpeg,image/webp"
                  onChange={(e) => setWatermarkImage(e.target.files[0] || null)}
                  className="hidden"
                />
              </div>
            </div>

            {/* Position Grid */}
            <div className="mb-4">
              <label className={`block text-sm font-medium mb-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>Position</label>
              <div className="grid grid-cols-3 gap-1 max-w-[180px] mx-auto">
                {['top-left', 'top-center', 'top-right', 'middle-left', 'center', 'middle-right', 'bottom-left', 'bottom-center', 'bottom-right'].map(pos => (
                  <button
                    key={pos}
                    onClick={() => setWatermarkPosition(pos)}
                    className={`w-14 h-14 rounded-lg border-2 transition-all flex items-center justify-center ${
                      watermarkPosition === pos
                        ? 'border-blue-500 bg-blue-100 dark:bg-blue-900'
                        : isDarkMode ? 'border-gray-600 bg-gray-700 hover:border-gray-500' : 'border-gray-200 bg-gray-50 hover:border-gray-400'
                    }`}
                  >
                    <div className={`w-3 h-3 rounded-full ${watermarkPosition === pos ? 'bg-blue-500' : isDarkMode ? 'bg-gray-500' : 'bg-gray-300'}`}></div>
                  </button>
                ))}
              </div>
            </div>

            {/* Opacity Slider */}
            <div className="mb-5">
              <label className={`block text-sm font-medium mb-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                Opacity: {watermarkOpacity}%
              </label>
              <input
                type="range"
                min="10"
                max="100"
                step="5"
                value={watermarkOpacity}
                onChange={(e) => setWatermarkOpacity(parseInt(e.target.value))}
                className="w-full h-2 accent-blue-500"
              />
            </div>

            {/* Buttons */}
            <div className="flex gap-3">
              <button
                onClick={() => { setShowWatermarkModal(false); setWatermarkImage(null); }}
                className={`flex-1 py-2 rounded-lg font-medium text-sm ${isDarkMode ? 'bg-gray-700 hover:bg-gray-600 text-white' : 'bg-gray-200 hover:bg-gray-300 text-gray-700'}`}
              >
                Cancel
              </button>
              <button
                onClick={applyWatermark}
                disabled={!watermarkImage || watermarkApplying}
                className="flex-1 py-2 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-lg font-medium text-sm disabled:opacity-50"
              >
                {watermarkApplying ? 'Applying...' : 'Apply Watermark'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ImageManager;
